import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import pitchDeckService from '../../services/pitchDeckService';
import * as comfyService from '../../services/comfyService';
import { DEFAULT_Z_IMAGE_WORKFLOW } from '../Storyboard';
import { getProjectScreenplayAnalyses } from '../../services/screenplayAnalysisService';
import jsPDF from 'jspdf';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Stack,
  List,
  ListItemButton,
  Avatar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GlobalStyles,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowDownward as MoveDownIcon,
  ArrowUpward as MoveUpIcon,
  AutoFixHigh as AiImageIcon,
  BorderColor as BorderColorIcon,
  Brush as ThemeIcon,
  CompareArrows as ComparablesIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  Download as DownloadIcon,
  DragIndicator as DragIcon,
  DriveFileMove as DriveFileMoveIcon,
  FlipToBack as SendToBackIcon,
  FlipToFront as BringToFrontIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignRight as AlignRightIcon,
  FormatBold as BoldIcon,
  FormatColorFill as FormatColorFillIcon,
  FormatColorText as FormatColorTextIcon,
  FormatItalic as ItalicIcon,
  FormatShapes as ShapeIcon,
  FormatUnderlined as UnderlineIcon,
  GridOn as MoodBoardIcon,
  Image as ImageIcon,
  OpenWith as OpenWithIcon,
  Palette as PaletteIcon,
  PictureAsPdf as PdfIcon,
  PieChart as BudgetIcon,
  Print as PrintIcon,
  RotateLeft as ResetIcon,
  TextFields as TextIcon,
  Timeline as TimelineIcon,
  ViewAgenda as TwoColumnIcon,
  ViewColumn as ColumnLayoutIcon,
  ViewDay as SingleColumnIcon,
  ViewModule as GridLayoutIcon,
  ViewQuilt as LayoutIcon
} from '@mui/icons-material';

// Professional Theme System
const PITCH_DECK_THEMES = {
  modern: {
    name: 'Modern Minimal',
    description: 'Clean, contemporary design with subtle accents',
    colors: {
      primary: '#2563eb',
      secondary: '#7c3aed',
      accent: '#06b6d4',
      background: '#ffffff',
      text: '#111827',
      textSecondary: '#6b7280'
    },
    fonts: {
      heading: 'Poppins',
      body: 'Inter'
    },
    slideDefaults: {
      backgroundColor: '#ffffff',
      headingColor: '#111827',
      bodyColor: '#374151',
      headingSize: 36,
      bodySize: 20
    }
  },
  cinematic: {
    name: 'Cinematic Dark',
    description: 'Bold, dramatic design for high-impact presentations',
    colors: {
      primary: '#dc2626',
      secondary: '#f59e0b',
      accent: '#eab308',
      background: '#0f172a',
      text: '#f8fafc',
      textSecondary: '#cbd5e1'
    },
    fonts: {
      heading: 'Playfair Display',
      body: 'Lora'
    },
    slideDefaults: {
      backgroundColor: '#0f172a',
      headingColor: '#f8fafc',
      bodyColor: '#e2e8f0',
      headingSize: 38,
      bodySize: 22
    }
  },
  indie: {
    name: 'Indie Film',
    description: 'Artistic, creative design with warm tones',
    colors: {
      primary: '#ea580c',
      secondary: '#84cc16',
      accent: '#facc15',
      background: '#fffbeb',
      text: '#292524',
      textSecondary: '#78716c'
    },
    fonts: {
      heading: 'Montserrat',
      body: 'Source Sans Pro'
    },
    slideDefaults: {
      backgroundColor: '#fffbeb',
      headingColor: '#292524',
      bodyColor: '#44403c',
      headingSize: 34,
      bodySize: 20
    }
  },
  corporate: {
    name: 'Corporate Professional',
    description: 'Traditional, trustworthy design for business pitches',
    colors: {
      primary: '#1e40af',
      secondary: '#059669',
      accent: '#0891b2',
      background: '#f9fafb',
      text: '#1f2937',
      textSecondary: '#6b7280'
    },
    fonts: {
      heading: 'Roboto',
      body: 'Roboto'
    },
    slideDefaults: {
      backgroundColor: '#f9fafb',
      headingColor: '#1f2937',
      bodyColor: '#374151',
      headingSize: 32,
      bodySize: 18
    }
  },
  vibrant: {
    name: 'Bold & Bright',
    description: 'Energetic, colorful design for dynamic projects',
    colors: {
      primary: '#ec4899',
      secondary: '#8b5cf6',
      accent: '#06b6d4',
      background: '#ffffff',
      text: '#18181b',
      textSecondary: '#52525b'
    },
    fonts: {
      heading: 'Montserrat',
      body: 'Poppins'
    },
    slideDefaults: {
      backgroundColor: '#ffffff',
      headingColor: '#18181b',
      bodyColor: '#3f3f46',
      headingSize: 36,
      bodySize: 21
    }
  }
};

// Page Layout System
const PAGE_LAYOUTS = {
  titleCentered: {
    name: 'Title Centered',
    description: 'Large centered title with subtitle',
    icon: 'SingleColumnIcon',
    template: (slide) => ({
      heading: {
        x: 80,
        y: 180,
        width: 800,
        height: 120,
        align: 'center',
        fontSize: 48
      },
      body: {
        x: 120,
        y: 320,
        width: 720,
        height: 140,
        align: 'center',
        fontSize: 24
      }
    })
  },
  titleLeft: {
    name: 'Title Left',
    description: 'Title on left, content on right',
    icon: 'TwoColumnIcon',
    template: (slide) => ({
      heading: {
        x: 60,
        y: 60,
        width: 360,
        height: 420,
        align: 'left',
        fontSize: 42
      },
      body: {
        x: 480,
        y: 80,
        width: 420,
        height: 380,
        align: 'left',
        fontSize: 20
      }
    })
  },
  splitScreen: {
    name: 'Split Screen',
    description: 'Two equal columns',
    icon: 'ColumnLayoutIcon',
    template: (slide) => ({
      heading: {
        x: 60,
        y: 40,
        width: 840,
        height: 60,
        align: 'center',
        fontSize: 36
      },
      leftColumn: {
        x: 60,
        y: 120,
        width: 400,
        height: 360,
        align: 'left',
        fontSize: 18
      },
      rightColumn: {
        x: 500,
        y: 120,
        width: 400,
        height: 360,
        align: 'left',
        fontSize: 18
      }
    })
  },
  fullContent: {
    name: 'Full Content',
    description: 'Title top, full-width content',
    icon: 'ViewDay',
    template: (slide) => ({
      heading: {
        x: 60,
        y: 50,
        width: 840,
        height: 80,
        align: 'left',
        fontSize: 40
      },
      body: {
        x: 60,
        y: 150,
        width: 840,
        height: 330,
        align: 'left',
        fontSize: 22
      }
    })
  },
  grid: {
    name: 'Grid Layout',
    description: '2x2 grid for images or content blocks',
    icon: 'GridLayoutIcon',
    template: (slide) => ({
      heading: {
        x: 60,
        y: 30,
        width: 840,
        height: 60,
        align: 'center',
        fontSize: 36
      },
      topLeft: {
        x: 60,
        y: 110,
        width: 400,
        height: 190,
        align: 'center',
        fontSize: 18
      },
      topRight: {
        x: 500,
        y: 110,
        width: 400,
        height: 190,
        align: 'center',
        fontSize: 18
      },
      bottomLeft: {
        x: 60,
        y: 320,
        width: 400,
        height: 190,
        align: 'center',
        fontSize: 18
      },
      bottomRight: {
        x: 500,
        y: 320,
        width: 400,
        height: 190,
        align: 'center',
        fontSize: 18
      }
    })
  },
  imageLeft: {
    name: 'Image Left',
    description: 'Large image on left, text on right',
    icon: 'TwoColumnIcon',
    template: (slide) => ({
      image: {
        x: 60,
        y: 60,
        width: 420,
        height: 420,
        align: 'center'
      },
      heading: {
        x: 520,
        y: 80,
        width: 380,
        height: 80,
        align: 'left',
        fontSize: 36
      },
      body: {
        x: 520,
        y: 180,
        width: 380,
        height: 280,
        align: 'left',
        fontSize: 20
      }
    })
  },
  imageRight: {
    name: 'Image Right',
    description: 'Text on left, large image on right',
    icon: 'TwoColumnIcon',
    template: (slide) => ({
      heading: {
        x: 60,
        y: 80,
        width: 380,
        height: 80,
        align: 'left',
        fontSize: 36
      },
      body: {
        x: 60,
        y: 180,
        width: 380,
        height: 280,
        align: 'left',
        fontSize: 20
      },
      image: {
        x: 480,
        y: 60,
        width: 420,
        height: 420,
        align: 'center'
      }
    })
  }
};

const TEMPLATE_CONFIGS = {
  film: {
    label: 'Film',
    slides: [
      { type: 'title', label: 'Title', placeholder: '', isTitle: true },
      {
        type: 'logline',
        label: 'Logline',
        placeholder: 'The concise logline of the film.',
        prefill: 'logline'
      },
      {
        type: 'overview',
        label: 'Film Overview',
        placeholder: 'Capture the hook, genre, setting, and tone of the film.',
      },
      {
        type: 'synopsis',
        label: 'Synopsis',
        placeholder: 'Provide a concise synopsis covering the beginning, middle, and end.',
        prefill: 'synopsis'
      },
      {
        type: 'characters',
        label: 'Key Characters',
        placeholder: 'Introduce the protagonist, antagonist, and essential supporting roles.'
      },
      {
        type: 'visual',
        label: 'Visual Style',
        placeholder: 'Describe the cinematography, color palette, references, and mood.'
      },
      {
        type: 'moodBoard',
        label: 'Mood Board',
        placeholder: 'Visual references and inspiration',
        layout: 'grid',
        gridColumns: 2
      },
      {
        type: 'comparables',
        label: 'Comparable Films',
        placeholder: 'Similar successful films in genre, tone, or market',
        layout: 'table'
      },
      {
        type: 'market',
        label: 'Target Audience & Market',
        placeholder: 'Outline primary audiences, comparable films, and distribution potential.'
      },
      {
        type: 'timeline',
        label: 'Production Timeline',
        placeholder: 'Key milestones and production schedule',
        layout: 'timeline'
      },
      {
        type: 'production',
        label: 'Production Plan',
        placeholder: 'Summarize schedule, locations, format, and key production milestones.'
      },
      {
        type: 'team',
        label: 'Team & Talent',
        placeholder: 'Highlight key creatives, cast attachments, and production partners.'
      },
      {
        type: 'budgetBreakdown',
        label: 'Budget Breakdown',
        placeholder: 'Detailed budget allocation by category',
        layout: 'chart',
        chartType: 'pie'
      },
      {
        type: 'ask',
        label: 'Budget & Ask',
        placeholder: 'Present budget overview, investment opportunity, and support required.'
      }
    ]
  },
  television: {
    label: 'Television',
    slides: [
      { type: 'title', label: 'Series Title', placeholder: '', isTitle: true },
      {
        type: 'seriesLogline',
        label: 'Series Logline',
        placeholder: 'Convey the core premise, protagonist, and hook of the series.',
        prefill: 'logline'
      },
      {
        type: 'seasonOverview',
        label: 'Season Overview',
        placeholder: 'Summarize the season arc, key conflicts, and emotional journey.',
        bodyFontSize: 20
      },
      {
        type: 'episodeGuide',
        label: 'Episode Guide',
        placeholder: 'Provide a short list of flagship episodes or season breakdown.'
      },
      {
        type: 'characters',
        label: 'Main Characters',
        placeholder: 'Introduce leads, ensemble roles, and their dynamics.'
      },
      {
        type: 'world',
        label: 'World & Tone',
        placeholder: 'Describe the setting, tone, comparable shows, and stylistic references.'
      },
      {
        type: 'audience',
        label: 'Audience & Platforms',
        placeholder: 'Identify target demographics, networks/streamers, and market positioning.'
      },
      {
        type: 'production',
        label: 'Production Plan',
        placeholder: 'Outline format, season structure, schedule, and production partners.'
      },
      {
        type: 'team',
        label: 'Creative Team',
        placeholder: 'Highlight showrunner, writers, directors, and key talent attachments.'
      }
    ]
  },
  custom: {
    label: 'Custom',
    slides: [
      { type: 'title', label: 'Project Title', placeholder: '', isTitle: true },
      {
        type: 'concept',
        label: 'Concept',
        placeholder: 'Outline your project concept, mission, or big idea.'
      },
      {
        type: 'blank',
        label: 'Custom Slide',
        placeholder: 'Add any content, visuals, or notes needed for your pitch.',
        headingFontSize: 28,
        bodyFontSize: 20
      }
    ]
  }
};

const DEFAULT_TEMPLATE_KEY = 'film';

const getTemplateDefinitions = (templateKey) =>
  TEMPLATE_CONFIGS[templateKey]?.slides ?? TEMPLATE_CONFIGS[DEFAULT_TEMPLATE_KEY].slides;

const SLIDE_CANVAS = { width: 960, height: 540 };
const SNAP = 10;
const FONT_FAMILIES = ['Poppins', 'Roboto', 'Lora', 'Playfair Display', 'Montserrat', 'Source Sans Pro'];

const createId = (prefix = 'el') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_TEXT_STYLE = {
  fontFamily: 'Poppins',
  fontSize: 28,
  fontWeight: 500,
  fontStyle: 'normal',
  textDecoration: 'none',
  color: '#111111',
  backgroundColor: 'transparent',
  lineHeight: 1.4,
  letterSpacing: 0
};

const DEFAULT_SHAPE_STYLE = {
  fill: '#2563eb',
  borderColor: '#1e293b',
  borderWidth: 3,
  borderStyle: 'solid',
  borderRadius: 24,
  opacity: 1
};

const createTextElement = (overrides = {}) => {
  // Calculate initial size based on text content if not provided
  const hasText = overrides.text && overrides.text.trim().length > 0;
  const autoFit = overrides.autoFit !== false; // Default to true
  
  return {
    id: createId('text'),
    type: 'text',
    x: overrides.x ?? SLIDE_CANVAS.width * 0.2,
    y: overrides.y ?? SLIDE_CANVAS.height * 0.25,
    width: overrides.width ?? (hasText && autoFit ? 'auto' : SLIDE_CANVAS.width * 0.6),
    height: overrides.height ?? (hasText && autoFit ? 'auto' : SLIDE_CANVAS.height * 0.18),
    rotation: overrides.rotation ?? 0,
    text: overrides.text ?? '',
    align: overrides.align ?? 'left',
    autoFit: autoFit,
    style: {
      ...DEFAULT_TEXT_STYLE,
      ...(overrides.style ?? {})
    }
  };
};

const createImageElement = (src, overrides = {}) => ({
  id: createId('image'),
  type: 'image',
  src,
  x: overrides.x ?? SLIDE_CANVAS.width * 0.2,
  y: overrides.y ?? SLIDE_CANVAS.height * 0.25,
  width: overrides.width ?? 300,
  height: overrides.height ?? 200,
  rotation: overrides.rotation ?? 0,
  objectFit: overrides.objectFit ?? 'cover',
  lockAspect: overrides.lockAspect ?? true,
  originalRatio: overrides.originalRatio ?? null
});

const createShapeElement = (overrides = {}) => ({
  id: createId('shape'),
  type: 'shape',
  x: overrides.x ?? SLIDE_CANVAS.width * 0.3,
  y: overrides.y ?? SLIDE_CANVAS.height * 0.3,
  width: overrides.width ?? 260,
  height: overrides.height ?? 160,
  rotation: overrides.rotation ?? 0,
  style: {
    ...DEFAULT_SHAPE_STYLE,
    ...(overrides.style ?? {})
  }
});

const getPrefillText = (definition, projectLogline, projectSynopsis) => {
  if (definition.prefill === 'logline') return projectLogline || '';
  if (definition.prefill === 'synopsis') return projectSynopsis || '';
  return '';
};

const createSlide = (definition, projectName, projectLogline, projectSynopsis) => {
  const slide = {
    id: createId(definition.type),
    type: definition.type,
    label: definition.label,
    elements: [],
    backgroundColor: '#ffffff'
  };

  if (definition.isTitle) {
    slide.elements = [
      createTextElement({
        text: projectName || 'Click to add title',
        x: SLIDE_CANVAS.width * 0.1,
        y: SLIDE_CANVAS.height * 0.4,
        align: 'center',
        style: { fontSize: 36 }
      })
    ];
  } else {
    const headingFontSize = definition.headingFontSize ?? 34;
    const bodyFontSize = definition.bodyFontSize ?? 22;
    const bodyPrefill = getPrefillText(definition, projectLogline, projectSynopsis);
    slide.elements = [
      createTextElement({
        text: definition.label,
        x: SLIDE_CANVAS.width * 0.08,
        y: SLIDE_CANVAS.height * 0.12,
        align: 'left',
        style: { fontSize: headingFontSize }
      }),
      createTextElement({
        text: bodyPrefill,
        x: SLIDE_CANVAS.width * 0.08,
        y: SLIDE_CANVAS.height * 0.3,
        align: 'left',
        style: { fontSize: bodyFontSize }
      })
    ];
  }

  return slide;
};

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1
});

const fullCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const formatCurrencyCompact = (value) => {
  if (!value || Number.isNaN(value)) return 'N/A';
  return compactCurrencyFormatter.format(value);
};

const formatCurrencyFull = (value) => {
  if (!value || Number.isNaN(value)) return 'N/A';
  return fullCurrencyFormatter.format(value);
};

const estimateTextBoxHeight = (text = '', fontSize = 20, lineHeight = 1.5, minHeightRatio = 0.22) => {
  const lines = text ? text.split('\n').length + 1 : 1;
  const linePx = fontSize * lineHeight;
  const contentHeight = lines * linePx * 1.1;
  const minHeight = SLIDE_CANVAS.height * minHeightRatio;
  return Math.max(minHeight, contentHeight);
};

const buildComparablesLayout = (films) => {
  if (!Array.isArray(films) || films.length === 0) return null;

  const safeNumber = (val) => {
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const avgNumber = (values) => {
    const filtered = values.filter((value) => value > 0);
    if (!filtered.length) return 0;
    return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
  };

  const totals = films.reduce(
    (acc, film) => {
      acc.budget += safeNumber(film.budget);
      acc.domestic += safeNumber(film.domesticBoxOffice);
      acc.international += safeNumber(film.internationalBoxOffice);
      return acc;
    },
    { budget: 0, domestic: 0, international: 0 }
  );

  const filmCount = films.length;
  const avgBudget = filmCount ? totals.budget / filmCount : 0;
  const avgDomestic = filmCount ? totals.domestic / filmCount : 0;
  const avgInternational = filmCount ? totals.international / filmCount : 0;
  const avgWorldwide = avgDomestic + avgInternational;

  const avgROI = avgBudget
    ? Math.round(((avgDomestic + avgInternational - avgBudget) / avgBudget) * 100)
    : 0;

  const maxDomestic = Math.max(...films.map((film) => safeNumber(film.domesticBoxOffice)));
  const maxWorldwide = Math.max(
    ...films.map(
      (film) =>
        safeNumber(film.domesticBoxOffice) + safeNumber(film.internationalBoxOffice)
    )
  );

  const metrics = [
    {
      label: 'AVERAGE BUDGET',
      value: formatCurrencyFull(avgBudget),
      detail: 'Prod spend'
    },
    {
      label: 'AVERAGE DOMESTIC',
      value: formatCurrencyFull(avgDomestic),
      detail: 'Domestic B.O.'
    },
    {
      label: 'AVERAGE INTERNATIONAL',
      value: formatCurrencyFull(avgInternational),
      detail: 'Intl B.O.'
    },
    {
      label: 'AVERAGE WORLDWIDE',
      value: formatCurrencyFull(avgWorldwide),
      detail: 'Total B.O.'
    },
    {
      label: 'AVERAGE ROI',
      value: `${avgROI > 0 ? '+' : ''}${avgROI}%`,
      detail: 'Return'
    },
    {
      label: 'TOP PERFORMER (WW)',
      value: formatCurrencyFull(maxWorldwide),
      detail: 'Peak gross'
    }
  ];

  const insightsText = [
    `Benchmark set from ${filmCount} comparable films.`,
    `Top domestic marker: ${formatCurrencyFull(maxDomestic)}.`,
    `Use these averages to frame budget vs. upside.`
  ].join('\n');

  const headline = `Avg Worldwide ${formatCurrencyFull(avgWorldwide)} across ${filmCount} comps`;

  return {
    metrics,
    insightsText,
    headline
  };
};

const buildMarketSummary = (analysis) => {
  if (!analysis) return null;

  const coverageReport =
    analysis.filmCoverageReport || analysis.analysis?.filmCoverageReport || {};
  const reportMarket =
    analysis.marketAnalysis ||
    analysis.analysis?.marketAnalysis ||
    analysis.analysis?.marketOverview ||
    {};

  const marketNarrative =
    reportMarket.marketOverview ||
    reportMarket.marketSummary ||
    coverageReport.marketAnalysis ||
    '';

  const audienceDetail =
    reportMarket.targetAudience ||
    coverageReport.targetAudience ||
    reportMarket.audience ||
    coverageReport.audienceAppeal ||
    'Target audience pending analysis.';

  const marketability =
    coverageReport.marketability || reportMarket.marketability || null;
  const boxOffice =
    reportMarket.boxOfficePotential || coverageReport.boxOfficePotential || null;
  const streaming = reportMarket.streamingPotential || null;
  const international = reportMarket.internationalAppeal || null;
  const franchise =
    reportMarket.franchisePotential || coverageReport.franchisePotential || null;

  const marketingMoves = Array.isArray(reportMarket.marketingRecommendations)
    ? reportMarket.marketingRecommendations.slice(0, 3)
    : [];

  const outlookLines = [
    marketability ? `Marketability: ${marketability}` : null,
    boxOffice ? `Box Office Potential: ${boxOffice}` : null,
    streaming ? `Streaming Appeal: ${streaming}` : null,
    international ? `International Appeal: ${international}` : null,
    franchise ? `Franchise/Series Potential: ${franchise}` : null
  ].filter(Boolean);

  return {
    analysisText: marketNarrative.trim() || null,
    audienceText: audienceDetail.trim(),
    outlookItems: outlookLines,
    marketingMoves
  };
};

const createInitialDeck = (
  projectId,
  projectName,
  projectLogline,
  projectSynopsis,
  templateKey = DEFAULT_TEMPLATE_KEY
) => {
  const definitions = getTemplateDefinitions(templateKey);
  const slides = definitions.map((definition) =>
    createSlide(definition, projectName, projectLogline, projectSynopsis)
  );

  return {
    id: projectId,
    title: projectName || '',
    logline: projectLogline || '',
    template: templateKey,
    slides,
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
};

const normalizeElement = (element) => {
  if (!element) return null;
  if (element.type === 'image') {
    return {
      ...element,
      id: element.id ?? createId('image'),
      src: element.src ?? '',
      x: Number.isFinite(element.x) ? element.x : SLIDE_CANVAS.width * 0.15,
      y: Number.isFinite(element.y) ? element.y : SLIDE_CANVAS.height * 0.2,
      width: Number.isFinite(element.width) ? element.width : SLIDE_CANVAS.width * 0.7,
      height: Number.isFinite(element.height) ? element.height : SLIDE_CANVAS.height * 0.5,
      rotation: element.rotation ?? 0,
      objectFit: element.objectFit ?? 'cover'
    };
  }

  if (element.type === 'shape') {
    return {
      ...element,
      id: element.id ?? createId('shape'),
      type: 'shape',
      x: Number.isFinite(element.x) ? element.x : SLIDE_CANVAS.width * 0.25,
      y: Number.isFinite(element.y) ? element.y : SLIDE_CANVAS.height * 0.3,
      width: Number.isFinite(element.width) ? element.width : SLIDE_CANVAS.width * 0.35,
      height: Number.isFinite(element.height) ? element.height : SLIDE_CANVAS.height * 0.25,
      rotation: element.rotation ?? 0,
      style: {
        ...DEFAULT_SHAPE_STYLE,
        ...(element.style ?? {})
      }
    };
  }

  const legacyText = element.text ?? element.content ?? '';
  const legacyStyle = element.style ?? {
    fontFamily: element.fontFamily,
    fontSize: element.fontSize,
    fontWeight: element.fontWeight,
    fontStyle: element.fontStyle,
    textDecoration: element.textDecoration,
    color: element.color,
    backgroundColor: element.backgroundColor,
    lineHeight: element.lineHeight,
    letterSpacing: element.letterSpacing
  };
  
  // Calculate width/height if set to 'auto'
  let width = element.width;
  let height = element.height;
  
  if (element.width === 'auto' || element.height === 'auto') {
    const style = { ...DEFAULT_TEXT_STYLE, ...legacyStyle };
    const measureDiv = document.createElement('div');
    measureDiv.style.position = 'absolute';
    measureDiv.style.visibility = 'hidden';
    measureDiv.style.whiteSpace = 'pre-wrap';
    measureDiv.style.wordBreak = 'break-word';
    measureDiv.style.fontFamily = style.fontFamily;
    measureDiv.style.fontSize = `${style.fontSize}px`;
    measureDiv.style.fontWeight = style.fontWeight;
    measureDiv.style.fontStyle = style.fontStyle;
    measureDiv.style.lineHeight = style.lineHeight;
    measureDiv.style.letterSpacing = `${style.letterSpacing}px`;
    measureDiv.style.padding = '4px 8px';
    measureDiv.textContent = legacyText || 'A';
    
    document.body.appendChild(measureDiv);
    if (element.width === 'auto') {
      width = Math.min(Math.ceil(measureDiv.offsetWidth) + 20, SLIDE_CANVAS.width * 0.9);
    }
    if (element.height === 'auto') {
      height = Math.ceil(measureDiv.offsetHeight) + 10;
    }
    document.body.removeChild(measureDiv);
  }
  
  return {
    ...element,
    id: element.id ?? createId('text'),
    type: 'text',
    text: legacyText,
    x: Number.isFinite(element.x) ? element.x : SLIDE_CANVAS.width * 0.2,
    y: Number.isFinite(element.y) ? element.y : SLIDE_CANVAS.height * 0.25,
    width: Number.isFinite(width) ? width : SLIDE_CANVAS.width * 0.6,
    height: Number.isFinite(height) ? height : SLIDE_CANVAS.height * 0.18,
    rotation: element.rotation ?? 0,
    align: element.align ?? 'left',
    autoFit: element.autoFit !== false,
    style: {
      ...DEFAULT_TEXT_STYLE,
      ...Object.fromEntries(
        Object.entries(legacyStyle || {}).filter(([, value]) => value !== undefined && value !== null)
      )
    }
  };
};

const normalizeSlide = (definition, existing, projectName, projectLogline, projectSynopsis) => {
  const fallback = createSlide(definition, projectName, projectLogline, projectSynopsis);
  if (!existing) {
    return fallback;
  }

  const backgroundColor = existing.backgroundColor ?? fallback.backgroundColor ?? '#ffffff';

  if (Array.isArray(existing.elements) && existing.elements.length) {
    return {
      ...fallback,
      ...existing,
      backgroundColor,
      elements: existing.elements.map(normalizeElement).filter(Boolean)
    };
  }

  if (definition.isTitle) {
    const [titleEl] = fallback.elements;
    return {
      ...fallback,
      backgroundColor,
      elements: [
        { ...titleEl, text: existing.content?.title ?? existing.content?.[definition.type] ?? titleEl.text }
      ]
    };
  }

  const [headingEl, bodyEl] = fallback.elements;
  return {
    ...fallback,
    backgroundColor,
    elements: [
      { ...headingEl, text: existing.content?.heading ?? headingEl.text },
      {
        ...bodyEl,
        text:
          existing.content?.body ??
          existing.content?.[definition.type] ??
          bodyEl.text
      }
    ]
  };
};

const normalizeSlides = (slides, definitions, projectName, projectLogline, projectSynopsis) =>
  definitions.map((definition, index) =>
    normalizeSlide(definition, slides?.[index], projectName, projectLogline, projectSynopsis)
  );

const PitchDeck = ({ projectId, projectName = '', projectLogline = '', projectSynopsis = '' }) => {
  const theme = useTheme();
  const storageKey = useMemo(() => `pitch-deck-${projectId}`, [projectId]);
  const fileInputRef = useRef(null);
  const slideBackgroundInputRef = useRef(null);
  const textColorInputRef = useRef(null);
  const textBackgroundInputRef = useRef(null);
  const shapeFillInputRef = useRef(null);
  const shapeBorderInputRef = useRef(null);
  const shapeImageInputRef = useRef(null);
  const manualDeselectRef = useRef(false);
  const suppressDeselectRef = useRef(false);
  const [deck, setDeck] = useState(() =>
    createInitialDeck(projectId, projectName, projectLogline, projectSynopsis)
  );
  const prevProjectNameRef = useRef(projectName);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [editingElementId, setEditingElementId] = useState(null);
  const [hoveredElementId, setHoveredElementId] = useState(null);
  const [showHiddenSceneMetadata, setShowHiddenSceneMetadata] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const [dragOverlay, setDragOverlay] = useState(null); // {x,y,w,h}
  const [addSlideAnchorEl, setAddSlideAnchorEl] = useState(null);
  const [draggedSlideIndex, setDraggedSlideIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [aiImageDialogOpen, setAiImageDialogOpen] = useState(false);
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [aiImageMode] = useState('z-image'); // default to z-image
  const [aiShotSize, setAiShotSize] = useState('');
  const [aiLens, setAiLens] = useState('');
  const [aiAngle, setAiAngle] = useState('');
  const [aiAspectRatio, setAiAspectRatio] = useState('');
  const [aiTimeOfDay, setAiTimeOfDay] = useState('');
  const [aiAtmosphere, setAiAtmosphere] = useState('');
  const [aiLighting, setAiLighting] = useState('');
  const [aiComposition, setAiComposition] = useState('');
  const [aiCompositionFocus, setAiCompositionFocus] = useState('');
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [copyMoveDialogOpen, setCopyMoveDialogOpen] = useState(false);
  const [copyMoveMode, setCopyMoveMode] = useState('copy');
  const [copyMoveTargetIndex, setCopyMoveTargetIndex] = useState(0);
  const [lockImageAspect, setLockImageAspect] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('modern');
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState('fullContent');
  const [imageAdjustDialogOpen, setImageAdjustDialogOpen] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [slideZoom, setSlideZoom] = useState(1.5);
  const imageAdjustRef = useRef(null);
  const textInputRefs = useRef({});

  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const textMeasureRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const hasLoadedDataRef = useRef(false);
  const [analysisComparables, setAnalysisComparables] = useState([]);
  const [latestAnalysis, setLatestAnalysis] = useState(null);
  const [comparablesLoaded, setComparablesLoaded] = useState(false);

  // Load pitch deck from API with localStorage fallback
  useEffect(() => {
    const loadPitchDeck = async () => {
      try {
        // Try to load from API first
        const apiDeck = await pitchDeckService.getPitchDeck(projectId);
        
        if (apiDeck) {
          console.log('Loaded pitch deck from API');
          const templateKey = apiDeck.template ?? DEFAULT_TEMPLATE_KEY;
          const definitions = getTemplateDefinitions(templateKey);
          const slides = normalizeSlides(
            apiDeck.slides,
            definitions,
            projectName,
            projectLogline,
            projectSynopsis
          );

          setDeck({
            ...createInitialDeck(projectId, projectName, projectLogline, projectSynopsis, templateKey),
            ...apiDeck,
            id: projectId,
            title: projectName || apiDeck.title || '',
            logline: projectLogline || apiDeck.logline || '',
            template: templateKey,
            slides
          });
          setLastSaved(new Date(apiDeck.updatedAt));
          hasLoadedDataRef.current = true;
          // Mark initial load as complete after data is loaded
          setTimeout(() => {
            isInitialLoadRef.current = false;
          }, 100);
          return;
        }
      } catch (error) {
        const status = error.response?.status;
        console.log('🔍 No pitch deck found in API (status:', status, '), checking localStorage...');
        
        // Fallback to localStorage if API fails or returns 404/401, or if no response
        if (!error.response || status === 404 || status === 401) {
          try {
            const saved = localStorage.getItem(storageKey);
            console.log('localStorage key:', storageKey, 'has data:', !!saved);
            
            if (saved) {
              const parsed = JSON.parse(saved);
              console.log('Parsed localStorage data:', { 
                hasSlides: !!parsed.slides, 
                slideCount: parsed.slides?.length,
                title: parsed.title 
              });
              
              const templateKey = parsed.template ?? DEFAULT_TEMPLATE_KEY;
              const definitions = getTemplateDefinitions(templateKey);
              const slides = normalizeSlides(
                parsed.slides,
                definitions,
                projectName,
                projectLogline,
                projectSynopsis
              );

              setDeck({
                ...createInitialDeck(projectId, projectName, projectLogline, projectSynopsis, templateKey),
                ...parsed,
                id: projectId,
                title: projectName || parsed.title || '',
                logline: projectLogline || parsed.logline || '',
                template: templateKey,
                slides
              });
              hasLoadedDataRef.current = true;
              console.log('✅ Loaded pitch deck from localStorage with', slides.length, 'slides');
            } else {
              console.log('⚠️ No data in localStorage, using default deck');
            }
          } catch (localError) {
            console.error('❌ Error loading from localStorage:', localError);
          }
        }
      }
      
      // Mark initial load as complete after load attempt finishes
      setTimeout(() => {
        isInitialLoadRef.current = false;
        console.log('Initial load complete, auto-save enabled');
      }, 100);
    };

    loadPitchDeck();
  }, [projectId, projectName, projectLogline, projectSynopsis, storageKey]);

  useEffect(() => {
    if (!projectName) return;
    setDeck((prev) => {
      const lastSyncedName = prevProjectNameRef.current;
      if (prev.title && prev.title !== lastSyncedName) {
        // User manually customized the deck title; don't overwrite.
        return prev;
      }
      prevProjectNameRef.current = projectName;
      return { ...prev, title: projectName };
    });
  }, [projectName]);

  useEffect(() => {
    let isMounted = true;
    const loadComparables = async () => {
      if (!projectId) {
        setAnalysisComparables([]);
        setLatestAnalysis(null);
        setComparablesLoaded(true);
        return;
      }
      try {
        const analyses = await getProjectScreenplayAnalyses(projectId);
        if (!isMounted) return;

        const completedAnalyses = (analyses || [])
          .filter((item) => ['completed', 'completed_partial'].includes(item.status))
          .sort((a, b) => {
            const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return bDate - aDate;
          });

        const latest = completedAnalyses[0];
        setLatestAnalysis(latest || null);

        const latestWithComparables = completedAnalyses.find(
          (item) => Array.isArray(item?.comparableFilms) && item.comparableFilms.length > 0
        );

        setAnalysisComparables(latestWithComparables?.comparableFilms || []);
      } catch (error) {
        console.error('Failed to load screenplay comparables for pitch deck:', error);
        if (isMounted) {
          setAnalysisComparables([]);
          setLatestAnalysis(null);
        }
      } finally {
        if (isMounted) {
          setComparablesLoaded(true);
        }
      }
    };

    // Load immediately on mount and also poll periodically to catch new analyses
    loadComparables();
    const intervalId = setInterval(loadComparables, 30000); // refresh every 30s

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [projectId]);

  useEffect(() => {
    if (!comparablesLoaded) return;
    if (!analysisComparables || analysisComparables.length === 0) return;

    const layout = buildComparablesLayout(analysisComparables);
    if (!layout) return;

    setDeck((prev) => {
      const prevSlides = prev.slides ?? [];
      const definitions = getTemplateDefinitions(prev.template ?? DEFAULT_TEMPLATE_KEY);
      const comparablesDefinition = definitions.find((def) => def.type === 'comparables');
      if (!comparablesDefinition) return prev;

      let slides = [...prevSlides];
      let slideIndex = slides.findIndex((slideItem) => slideItem.type === 'comparables');
      let targetSlide;

      if (slideIndex === -1) {
        targetSlide = createSlide(
          comparablesDefinition,
          prev.title || projectName,
          prev.logline || projectLogline,
          projectSynopsis
        );
        slides = [...slides, targetSlide];
        slideIndex = slides.length - 1;
      } else {
        targetSlide = { ...slides[slideIndex], elements: [] };
      }

      const updatedElements = [];

      // Hero ribbon background
      updatedElements.push(
        createShapeElement({
          x: SLIDE_CANVAS.width * 0.05,
          y: SLIDE_CANVAS.height * 0.05,
          width: SLIDE_CANVAS.width * 0.9,
          height: SLIDE_CANVAS.height * 0.18,
          style: {
            fill: '#0f172a',
            borderColor: 'transparent',
            borderRadius: 36,
            opacity: 1
          }
        })
      );

      // Headline
      updatedElements.push(
        createTextElement({
          text: layout.headline?.toUpperCase() || comparablesDefinition.label.toUpperCase(),
          x: SLIDE_CANVAS.width * 0.055,
          y: SLIDE_CANVAS.height * 0.08,
          height: SLIDE_CANVAS.height * 0.12,
          width: SLIDE_CANVAS.width * 0.89,
          autoFit: false,
          align: 'center',
          style: {
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: 1,
            lineHeight: 1.15,
            color: '#f8fafc'
          }
        })
      );

      // Metrics grid cards
      const metricWidth = SLIDE_CANVAS.width * 0.26;
      const metricHeight = SLIDE_CANVAS.height * 0.17;
      const metricSpacingX = SLIDE_CANVAS.width * 0.04;
      const metricSpacingY = SLIDE_CANVAS.height * 0.035;
      const metricBaseX = SLIDE_CANVAS.width * 0.08;
      const metricBaseY = SLIDE_CANVAS.height * 0.25;
      const metricPalette = ['#e0f2fe', '#fee2e2', '#fef3c7', '#e0e7ff', '#dcfce7', '#f1f5f9'];

      layout.metrics.forEach((metric, index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);
        const cardX = metricBaseX + column * (metricWidth + metricSpacingX);
        const cardY = metricBaseY + row * (metricHeight + metricSpacingY);
        const fillColor = metricPalette[index % metricPalette.length];

        updatedElements.push(
          createShapeElement({
            x: cardX,
            y: cardY,
            width: metricWidth,
            height: metricHeight,
            style: {
              fill: fillColor,
              borderColor: 'transparent',
              borderRadius: 26,
              opacity: 1
            }
          })
        );

        updatedElements.push(
          createTextElement({
            text: `${metric.label}\n${metric.value}\n${metric.detail}`,
            x: cardX + SLIDE_CANVAS.width * 0.012,
            y: cardY + SLIDE_CANVAS.height * 0.012,
            width: metricWidth - SLIDE_CANVAS.width * 0.024,
            height: metricHeight - SLIDE_CANVAS.height * 0.024,
            autoFit: false,
            style: {
              fontSize: 15.5,
              lineHeight: 1.24,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: '#0f172a',
              textTransform: 'uppercase'
            }
          })
        );
      });

      // Insights block background
      updatedElements.push(
        createShapeElement({
          x: SLIDE_CANVAS.width * 0.05,
          y: SLIDE_CANVAS.height * 0.7,
          width: SLIDE_CANVAS.width * 0.9,
          height: SLIDE_CANVAS.height * 0.2,
          style: {
            fill: '#111827',
            borderColor: 'transparent',
            borderRadius: 30,
            opacity: 1
          }
        })
      );

      // Insights text
      updatedElements.push(
        createTextElement({
          text: layout.insightsText,
          x: SLIDE_CANVAS.width * 0.08,
          y: SLIDE_CANVAS.height * 0.73,
          width: SLIDE_CANVAS.width * 0.84,
          height: SLIDE_CANVAS.height * 0.16,
          autoFit: false,
          style: {
            fontSize: 18,
            lineHeight: 1.35,
            fontWeight: 500,
            color: '#e2e8f0'
          }
        })
      );

      const updatedSlide = { ...targetSlide, elements: updatedElements };
      slides[slideIndex] = updatedSlide;

      return { ...prev, slides };
    });
  }, [
    analysisComparables,
    comparablesLoaded,
    projectLogline,
    projectName,
    projectSynopsis
  ]);

  useEffect(() => {
    if (!comparablesLoaded) return;
    if (!latestAnalysis) return;

    const marketSummary = buildMarketSummary(latestAnalysis);
    if (!marketSummary) return;

    setDeck((prev) => {
      const prevSlides = prev.slides ?? [];
      const definitions = getTemplateDefinitions(prev.template ?? DEFAULT_TEMPLATE_KEY);
      const marketDefinition = definitions.find((def) => def.type === 'market');
      if (!marketDefinition) return prev;

      let slides = [...prevSlides];
      let slideIndex = slides.findIndex((slideItem) => slideItem.type === 'market');
      let targetSlide;

      if (slideIndex === -1) {
        targetSlide = createSlide(
          marketDefinition,
          prev.title || projectName,
          prev.logline || projectLogline,
          projectSynopsis
        );
        slides = [...slides, targetSlide];
        slideIndex = slides.length - 1;
      } else {
        targetSlide = { ...slides[slideIndex], elements: [...(slides[slideIndex].elements ?? [])] };
      }

      const sections = [
        {
          label: 'MARKET ANALYSIS',
          text: marketSummary.analysisText,
          position: { x: 0.08, y: 0.2 }
        },
        {
          label: 'TARGET AUDIENCE',
          text: marketSummary.audienceText,
          position: { x: 0.55, y: 0.2 }
        },
        {
          label: 'MARKET OUTLOOK',
          text: marketSummary.outlookItems?.map((item) => `• ${item}`).join('\n'),
          position: { x: 0.08, y: 0.55 }
        },
        {
          label: 'GO-TO-MARKET MOVES',
          text:
            marketSummary.marketingMoves?.length > 0
              ? marketSummary.marketingMoves.map((move) => `• ${move}`).join('\n')
              : null,
          position: { x: 0.55, y: 0.55 }
        }
      ];

      const filteredSections = sections.filter((section) => section.text);

      const sectionElements = filteredSections.map((section) => {
        const combinedText = `${section.label}\n${section.text}`;
        return createTextElement({
          text: combinedText,
          x: SLIDE_CANVAS.width * section.position.x,
          y: SLIDE_CANVAS.height * section.position.y,
          width: SLIDE_CANVAS.width * 0.35,
          height: estimateTextBoxHeight(combinedText, marketDefinition.bodyFontSize ?? 20),
          autoFit: false,
          align: 'left',
          style: {
            fontSize: marketDefinition.bodyFontSize ?? 20,
            lineHeight: 1.5,
            letterSpacing: 0.4,
            fontWeight: 500,
            color: '#0f172a'
          }
        });
      });

      const updatedElements = [
        createTextElement({
          text: marketDefinition.label,
          x: SLIDE_CANVAS.width * 0.08,
          y: SLIDE_CANVAS.height * 0.1,
          width: SLIDE_CANVAS.width * 0.84,
          height: SLIDE_CANVAS.height * 0.08,
          autoFit: false,
          align: 'left',
          style: { fontSize: marketDefinition.headingFontSize ?? 34, letterSpacing: 1.4 }
        }),
        ...sectionElements
      ];

      const updatedSlide = { ...targetSlide, elements: updatedElements };
      slides[slideIndex] = updatedSlide;

      return { ...prev, slides };
    });
  }, [
    comparablesLoaded,
    latestAnalysis,
    projectLogline,
    projectName,
    projectSynopsis
  ]);

  // Auto-save to API with debouncing and localStorage backup
  useEffect(() => {
    // Skip saving during initial load
    if (isInitialLoadRef.current) {
      console.log('Skipping save during initial load');
      return;
    }

    // Always save to localStorage immediately for offline access
    try {
      localStorage.setItem(storageKey, JSON.stringify(deck));
      console.log('Saved to localStorage');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Debounce API save to avoid too many requests
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus('saving');

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const deckData = {
          title: deck.title,
          logline: deck.logline,
          template: deck.template,
          slides: deck.slides
        };

        console.log('Attempting to save to API...', deckData);
        await pitchDeckService.savePitchDeck(projectId, deckData);
        setSaveStatus('saved');
        setLastSaved(new Date());
        console.log('Pitch deck saved to API successfully');
      } catch (error) {
        console.error('Error saving to API:', error);
        setSaveStatus('error');
        // Don't show error to user - localStorage backup is working
      }
    }, 2000); // 2 second debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [deck, storageKey, projectId]);

  useEffect(() => {
    const slide = deck.slides[currentSlideIndex];
    if (!slide) {
      manualDeselectRef.current = false;
      setSelectedElementId(null);
      return;
    }

    setSelectedElementId((prev) => {
      if (prev && slide.elements.some((element) => element.id === prev)) {
        return prev;
      }

      if (manualDeselectRef.current) {
        manualDeselectRef.current = false;
        return prev;
      }

      return slide.elements[0]?.id ?? null;
    });
  }, [deck.slides, currentSlideIndex]);

  const templateDefinitions = useMemo(
    () => getTemplateDefinitions(deck.template ?? DEFAULT_TEMPLATE_KEY),
    [deck.template]
  );
  const slideDefinition = templateDefinitions[currentSlideIndex] ?? templateDefinitions[0];
  const slide = deck.slides[currentSlideIndex];
  const slideElements = slide?.elements ?? [];
  const selectedElementIndex = slideElements.findIndex((item) => item.id === selectedElementId);
  const isElementSelected = selectedElementIndex >= 0;
  const isImageSelected = isElementSelected && slideElements[selectedElementIndex]?.type === 'image';
  const isAtFront = isElementSelected && selectedElementIndex === slideElements.length - 1;
  const isAtBack = isElementSelected && selectedElementIndex === 0;
  const selectedElement = isElementSelected ? slideElements[selectedElementIndex] : null;
  const isTextSelected = selectedElement?.type === 'text';
  const isShapeSelected = selectedElement?.type === 'shape';
  const selectedTextStyle = isTextSelected
    ? { ...DEFAULT_TEXT_STYLE, ...(selectedElement.style ?? {}) }
    : DEFAULT_TEXT_STYLE;
  const selectedShapeStyle = isShapeSelected
    ? { ...DEFAULT_SHAPE_STYLE, ...(selectedElement.style ?? {}) }
    : DEFAULT_SHAPE_STYLE;
  const slideBackgroundColor = slide?.backgroundColor ?? '#ffffff';
  const backgroundColorValue =
    selectedTextStyle.backgroundColor && selectedTextStyle.backgroundColor !== 'transparent'
      ? selectedTextStyle.backgroundColor
      : '#ffffff';
  const isBold = selectedTextStyle.fontWeight >= 600;
  const isItalic = selectedTextStyle.fontStyle === 'italic';
  const hasUnderline = (selectedTextStyle.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration)?.includes('underline');

  const baseBorderColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300];
  const defaultActionBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.6)
    : theme.palette.common.white;
  const defaultHoverBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.primary.main, 0.2)
    : alpha(theme.palette.primary.main, 0.08);
  const disabledBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.3)
    : theme.palette.action.disabledBackground;
  const disabledColor = theme.palette.grey[500];

  const baseActionButtonSx = {
    borderRadius: 2.5,
    border: '1.5px solid',
    borderColor: baseBorderColor,
    bgcolor: defaultActionBg,
    color: theme.palette.text.primary,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
    '&:hover': {
      bgcolor: defaultHoverBg,
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(15, 23, 42, 0.12)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)'
    },
    '&.Mui-disabled': {
      color: disabledColor,
      borderColor: baseBorderColor,
      bgcolor: disabledBg,
      opacity: 1,
      transform: 'none'
    }
  };

  const toolbarIconSx = {
    ...baseActionButtonSx,
    width: 32,
    height: 32,
    padding: 0.5,
    '& svg': {
      fontSize: '1.25rem'
    },
    '&:hover': {
      ...baseActionButtonSx['&:hover'],
      color: theme.palette.primary.main,
      bgcolor: alpha(theme.palette.primary.main, 0.12)
    }
  };

  const layerButtonSx = {
    ...baseActionButtonSx,
    color: theme.palette.primary.main,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    bgcolor: alpha(theme.palette.primary.main, 0.04),
    '&:hover': {
      ...baseActionButtonSx['&:hover'],
      color: theme.palette.primary.contrastText,
      bgcolor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main
    }
  };

  const getFormatButtonSx = (active) => ({
    ...baseActionButtonSx,
    width: 32,
    height: 32,
    padding: 0.5,
    '& svg': {
      fontSize: '1.1rem'
    },
    borderColor: active ? theme.palette.primary.main : baseBorderColor,
    color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
    bgcolor: active ? theme.palette.primary.main : defaultActionBg,
    boxShadow: active ? '0 2px 6px rgba(37, 99, 235, 0.25)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
    '&:hover': {
      ...baseActionButtonSx['&:hover'],
      color: theme.palette.primary.contrastText,
      bgcolor: active ? theme.palette.primary.dark : theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      boxShadow: active ? '0 4px 10px rgba(37, 99, 235, 0.3)' : '0 4px 8px rgba(15, 23, 42, 0.12)'
    }
  });

  const updateDeck = useCallback((mutator) => {
    setDeck((prev) => {
      const updated = mutator(prev);
      return { ...updated, updatedAt: Date.now() };
    });
  }, []);

  const updateSlideElements = useCallback((elementId, mutator) => {
    updateDeck((prev) => {
      const slides = [...prev.slides];
      const targetSlide = { ...slides[currentSlideIndex] };
      const elements = targetSlide.elements.map((element) =>
        element.id === elementId ? mutator(element) : element
      );
      slides[currentSlideIndex] = { ...targetSlide, elements };
      return { ...prev, slides };
    });
  }, [currentSlideIndex, updateDeck]);

  const replaceSlideElements = useCallback((elements) => {
    updateDeck((prev) => {
      const slides = [...prev.slides];
      slides[currentSlideIndex] = {
        ...slides[currentSlideIndex],
        elements
      };
      return { ...prev, slides };
    });
  }, [currentSlideIndex, updateDeck]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedElementId) return;

      const currentSlide = deck.slides?.[currentSlideIndex];
      const element = currentSlide?.elements?.find(el => el.id === selectedElementId);
      if (!element) return;

      let deltaX = 0;
      let deltaY = 0;

      switch (event.key) {
        case 'ArrowUp':
          deltaY = -SNAP;
          break;
        case 'ArrowDown':
          deltaY = SNAP;
          break;
        case 'ArrowLeft':
          deltaX = -SNAP;
          break;
        case 'ArrowRight':
          deltaX = SNAP;
          break;
        default:
          return;
      }

      event.preventDefault();

      updateSlideElements(selectedElementId, (prev) => {
        const resolveSize = (val, fallback) => (typeof val === 'number' && Number.isFinite(val) ? val : fallback);
        const prevWidth = resolveSize(prev.width, 200);
        const prevHeight = resolveSize(prev.height, 120);

        const nextX = Math.min(Math.max(0, prev.x + deltaX), SLIDE_CANVAS.width - prevWidth);
        const nextY = Math.min(Math.max(0, prev.y + deltaY), SLIDE_CANVAS.height - prevHeight);
        return { ...prev, x: nextX, y: nextY, width: prevWidth, height: prevHeight };
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, deck.slides, currentSlideIndex, updateSlideElements]);

  const handleLoglineChange = (value) => {
    updateDeck((prev) => ({ ...prev, logline: value }));
  };

  const handleSlideBackgroundChange = (value) => {
    const nextColor = value || '#ffffff';
    updateDeck((prev) => {
      const slides = [...prev.slides];
      const current = slides[currentSlideIndex];
      if (!current) return prev;
      slides[currentSlideIndex] = { ...current, backgroundColor: nextColor };
      return { ...prev, slides };
    });
  };

  const handleTextStyleChange = (field, value) => {
    if (!isElementSelected) return;
    updateSlideElements(selectedElementId, (element) => {
      if (element.type !== 'text') return element;
      const nextStyle = {
        ...DEFAULT_TEXT_STYLE,
        ...(element.style ?? {}),
        [field]: value
      };
      return { ...element, style: nextStyle };
    });
  };

  const handleShapeStyleChange = (field, value) => {
    if (!isElementSelected) return;
    updateSlideElements(selectedElementId, (element) => {
      if (element.type !== 'shape') return element;
      const nextStyle = {
        ...DEFAULT_SHAPE_STYLE,
        ...(element.style ?? {}),
        [field]: value
      };
      return { ...element, style: nextStyle };
    });
  };

  const handleAlignChange = (_event, value) => {
    if (!value || !isElementSelected) return;
    updateSlideElements(selectedElementId, (element) => {
      if (element.type !== 'text') return element;
      return { ...element, align: value };
    });
  };

  const handleMoveSlide = (direction) => {
    updateDeck((prev) => {
      const canMoveUp = direction === 'up' && currentSlideIndex > 0;
      const canMoveDown =
        direction === 'down' && currentSlideIndex < prev.slides.length - 1;
      if (!canMoveUp && !canMoveDown) return prev;

      const targetIndex = direction === 'up' ? currentSlideIndex - 1 : currentSlideIndex + 1;
      const slides = [...prev.slides];
      [slides[currentSlideIndex], slides[targetIndex]] = [slides[targetIndex], slides[currentSlideIndex]];
      setCurrentSlideIndex(targetIndex);
      return { ...prev, slides };
    });
  };

  const handleResetDeck = (templateKey = deck.template ?? DEFAULT_TEMPLATE_KEY) => {
    setDeck(createInitialDeck(projectId, projectName, projectLogline, projectSynopsis, templateKey));
    setCurrentSlideIndex(0);
    setSelectedElementId(null);
  };

  const handleTemplateChange = (templateKey) => {
    handleResetDeck(templateKey);
  };

  const handleThemeChange = (themeKey) => {
    setSelectedTheme(themeKey);
    const theme = PITCH_DECK_THEMES[themeKey];
    if (!theme) return;

    // Apply theme to all slides
    updateDeck((prev) => {
      const slides = prev.slides.map((slide) => ({
        ...slide,
        backgroundColor: theme.slideDefaults.backgroundColor,
        elements: slide.elements.map((element) => {
          if (element.type === 'text') {
            const isHeading = element.style?.fontSize >= 28;
            return {
              ...element,
              style: {
                ...element.style,
                fontFamily: isHeading ? theme.fonts.heading : theme.fonts.body,
                color: isHeading ? theme.slideDefaults.headingColor : theme.slideDefaults.bodyColor
              }
            };
          }
          return element;
        })
      }));
      return { ...prev, slides, theme: themeKey };
    });
    setThemeDialogOpen(false);
  };

  const handleLayoutChange = (layoutKey) => {
    setSelectedLayout(layoutKey);
    const layout = PAGE_LAYOUTS[layoutKey];
    if (!layout) return;

    // Apply layout to current slide only
    const currentSlide = deck.slides[currentSlideIndex];
    if (!currentSlide) return;

    const layoutTemplate = layout.template(currentSlide);
    const newElements = [];

    // Create text elements based on layout template
    Object.entries(layoutTemplate).forEach(([key, config]) => {
      if (key === 'image') {
        // Create placeholder for image
        newElements.push({
          id: `${Date.now()}-${key}`,
          type: 'shape',
          x: config.x,
          y: config.y,
          width: config.width,
          height: config.height,
          isImagePlaceholder: true, // Mark as image placeholder
          style: {
            fill: '#e5e7eb',
            borderColor: '#9ca3af',
            borderWidth: 2,
            borderStyle: 'dashed',
            borderRadius: 8,
            opacity: 1
          }
        });
      } else {
        // Create text element with auto-fit enabled
        const textContent = key.includes('heading') || key === 'heading' 
          ? currentSlide.heading || 'Heading' 
          : currentSlide.body || `${key.charAt(0).toUpperCase() + key.slice(1)} content`;
        
        newElements.push(createTextElement({
          text: textContent,
          x: config.x,
          y: config.y,
          align: config.align,
          style: {
            fontSize: config.fontSize
          }
        }));
      }
    });

    // Update current slide with new layout
    updateDeck((prev) => {
      const slides = [...prev.slides];
      slides[currentSlideIndex] = {
        ...currentSlide,
        elements: newElements,
        layout: layoutKey
      };
      return { ...prev, slides };
    });

    setLayoutDialogOpen(false);
  };

  const handleExportPDF = useCallback(async () => {
    try {
      // Create PDF in landscape mode (16:9 aspect ratio)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [960, 540] // Match slide canvas dimensions
      });

      // Get all slide elements from the print area
      const printArea = document.querySelector('.pitchdeck-print-area');
      if (!printArea) {
        console.error('Print area not found');
        return;
      }

      const slideElements = printArea.querySelectorAll('.pitchdeck-print-slide');
      
      for (let i = 0; i < slideElements.length; i++) {
        const slideElement = slideElements[i];
        
        // Capture slide as canvas using html2canvas (if available) or fallback
        try {
          // Try to use html2canvas if available
          if (window.html2canvas) {
            const canvas = await window.html2canvas(slideElement, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: null
            });
            
            const imgData = canvas.toDataURL('image/png');
            
            if (i > 0) {
              pdf.addPage();
            }
            
            pdf.addImage(imgData, 'PNG', 0, 0, 960, 540);
          } else {
            // Fallback: use print functionality
            console.warn('html2canvas not available, using print fallback');
            window.print();
            return;
          }
        } catch (error) {
          console.error('Error capturing slide:', error);
        }
      }

      // Save the PDF
      const fileName = `${deck.title || 'pitch-deck'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      // Fallback to print
      window.print();
    }
  }, [deck.title]);

  const handlePrintDeck = useCallback(() => {
    window.print();
  }, []);

  const handleExportSelectedImage = useCallback(async () => {
    const currentSlide = deck.slides?.[currentSlideIndex];
    const element = currentSlide?.elements?.find((el) => el.id === selectedElementId);
    if (!element || element.type !== 'image') return;

    try {
      const downloadDataUrl = async (dataUrl) => {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${deck.title || 'pitch-deck'}-image.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      };

      if (element.src?.startsWith('data:')) {
        await downloadDataUrl(element.src);
        return;
      }

      const response = await fetch(element.src);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      await downloadDataUrl(objectUrl);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error exporting image element:', error);
    }
  }, [currentSlideIndex, deck.slides, deck.title, selectedElementId]);

  const handleAddSlideOpen = (event) => {
    setAddSlideAnchorEl(event.currentTarget);
  };

  const handleAddSlideClose = () => {
    setAddSlideAnchorEl(null);
  };

  const addSlideFromDefinition = (definition) => {
    const newSlide = createSlide(
      definition,
      deck.title || projectName,
      deck.logline || projectLogline,
      projectSynopsis
    );

    const insertIndex = currentSlideIndex + 1;
    updateDeck((prev) => {
      const slides = [...prev.slides];
      slides.splice(insertIndex, 0, newSlide);
      return { ...prev, slides };
    });
    setCurrentSlideIndex(insertIndex);
    setSelectedElementId(newSlide.elements[0]?.id ?? null);
  };

  const handleAddSlide = (type) => {
    handleAddSlideClose();
    const definitions = templateDefinitions;
    const fallbackDefinition = {
      type,
      label: 'Custom Slide',
      placeholder: 'Add any content needed for your pitch.',
      headingFontSize: 32,
      bodyFontSize: 20
    };
    const definition =
      definitions.find((item) => item.type === type) ||
      definitions.find((item) => !item.isTitle) ||
      fallbackDefinition;
    addSlideFromDefinition(definition);
  };

  const handleAddCustomSlide = () => {
    handleAddSlideClose();
    addSlideFromDefinition({
      type: 'custom',
      label: 'Custom Slide',
      placeholder: 'Use this space for any content or visuals.',
      headingFontSize: 32,
      bodyFontSize: 20
    });
  };

  const handleDeleteSlide = () => {
    if (deck.slides.length <= 1) return;
    updateDeck((prev) => {
      const slides = prev.slides.filter((_, index) => index !== currentSlideIndex);
      const nextIndex = Math.max(0, currentSlideIndex - (currentSlideIndex === prev.slides.length - 1 ? 1 : 0));
      setCurrentSlideIndex(nextIndex);
      return { ...prev, slides };
    });
    setSelectedElementId(null);
  };

  const handleAddText = () => {
    const newElement = createTextElement({ text: 'Click to add text', align: 'left' });
    const elements = [...(slide?.elements ?? []), newElement];
    replaceSlideElements(elements);
    setSelectedElementId(newElement.id);
  };

  const handleAddShape = () => {
    const newElement = createShapeElement();
    const elements = [...(slide?.elements ?? []), newElement];
    replaceSlideElements(elements);
    setSelectedElementId(newElement.id);
  };

  const handleImageRequest = () => {
    fileInputRef.current?.click();
  };

  const handleSlideDragStart = (event, index) => {
    setDraggedSlideIndex(index);
    setDragOverIndex(index);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  };

  const handleSlideDragEnter = (event, index) => {
    event.preventDefault();
    if (draggedSlideIndex === null || draggedSlideIndex === index) return;
    setDragOverIndex(index);
  };

  const handleSlideDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };

  const handleSlideDragEnd = () => {
    setDraggedSlideIndex(null);
    setDragOverIndex(null);
  };

  const handleSlideDrop = (event, index) => {
    event.preventDefault();
    const sourceIndexData = event.dataTransfer?.getData('text/plain');
    const sourceIndex =
      draggedSlideIndex !== null
        ? draggedSlideIndex
        : sourceIndexData
        ? Number(sourceIndexData)
        : null;
    if (sourceIndex === null || !Number.isFinite(sourceIndex) || sourceIndex === index) {
      handleSlideDragEnd();
      return;
    }

    const currentSlideId = deck.slides[sourceIndex]?.id;
    let reorderedSlides = null;
    updateDeck((prev) => {
      const slides = [...prev.slides];
      const [moved] = slides.splice(sourceIndex, 1);
      slides.splice(index, 0, moved);
      reorderedSlides = slides;
      return { ...prev, slides };
    });

    if (currentSlideId && reorderedSlides) {
      const newIndex = reorderedSlides.findIndex((slide) => slide.id === currentSlideId);
      if (newIndex >= 0) {
        setCurrentSlideIndex(newIndex);
      }
    } else {
      setCurrentSlideIndex(index);
    }

    handleSlideDragEnd();
  };

  const handleImageSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      // Create a temporary image to get dimensions
      const img = new Image();
      img.onload = () => {
        const maxWidth = 400;
        const maxHeight = 300;
        let { width, height } = img;

        // Scale down if larger than max
        if (width > maxWidth || height > maxHeight) {
          const scale = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const newElement = createImageElement(src, { width, height });
        const elements = [...(slide?.elements ?? []), newElement];
        replaceSlideElements(elements);
        setSelectedElementId(newElement.id);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const handleImagePlaceholderClick = (placeholderId) => {
    // Store the placeholder ID to replace it after upload
    const placeholder = slideElements.find(el => el.id === placeholderId);
    if (!placeholder) return;

    // Create a temporary file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = () => {
        const src = typeof reader.result === 'string' ? reader.result : '';
        const img = new Image();
        img.onload = () => {
          // Use placeholder dimensions
          const { width, height, x, y } = placeholder;
          
          // Replace placeholder with actual image
          const newElement = createImageElement(src, { 
            width, 
            height, 
            x, 
            y,
            objectFit: 'cover'
          });
          
          // Replace the placeholder element with the image
          updateDeck((prev) => {
            const slides = [...prev.slides];
            const targetSlide = { ...slides[currentSlideIndex] };
            const elements = targetSlide.elements.map((element) =>
              element.id === placeholderId ? newElement : element
            );
            slides[currentSlideIndex] = { ...targetSlide, elements };
            return { ...prev, slides };
          });
          
          setSelectedElementId(newElement.id);
        };
        img.src = src;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleShapeImageUpload = () => {
    shapeImageInputRef.current?.click();
  };

  const handleShapeImageSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedElementId) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      const img = new Image();
      img.onload = () => {
        // Get current shape and preserve all its properties
        const currentShape = slideElements.find(el => el.id === selectedElementId);
        if (!currentShape || currentShape.type !== 'shape') return;
        
        // Add image to the shape while keeping all shape properties
        updateSlideElements(selectedElementId, (element) => ({
          ...element,
          backgroundImage: src,
          objectFit: 'cover', // How the image fits within the shape
          imageScale: 100, // Default scale 100%
          imagePositionX: 50, // Center X (0-100%)
          imagePositionY: 50  // Center Y (0-100%)
        }));
        
        // Clear the input
        if (shapeImageInputRef.current) {
          shapeImageInputRef.current.value = '';
        }
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const handleAiImageRequest = () => {
    setAiImageDialogOpen(true);
  };

  const handleAiImageDialogClose = () => {
    setAiImageDialogOpen(false);
    setAiImagePrompt('');
    setAiShotSize('');
    setAiLens('');
    setAiAngle('');
    setAiAspectRatio('');
    setAiTimeOfDay('');
    setAiAtmosphere('');
    setAiLighting('');
    setAiComposition('');
    setAiCompositionFocus('');
  };

  const aiShotOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Extreme Wide Shot', label: 'Extreme Wide' },
    { value: 'Wide Shot', label: 'Wide' },
    { value: 'Cowboy Shot', label: 'Cowboy' },
    { value: 'Full Shot', label: 'Full' },
    { value: 'Medium Shot', label: 'Medium' },
    { value: 'Medium Close-Up', label: 'Medium Close-Up' },
    { value: 'Close-Up', label: 'Close-Up' },
    { value: 'Extreme Close-Up', label: 'Extreme Close-Up' },
  ]), []);
  const ruleOfThirdsFocusOptions = useMemo(() => ([
    { value: '', label: 'No focus' },
    { value: 'center punch', label: 'Center punch' },
    { value: 'top-left intersection', label: 'Top left' },
    { value: 'top-right intersection', label: 'Top right' },
    { value: 'bottom-left intersection', label: 'Bottom left' },
    { value: 'bottom-right intersection', label: 'Bottom right' },
    { value: 'left vertical line', label: 'Left vertical' },
    { value: 'right vertical line', label: 'Right vertical' },
    { value: 'top horizontal line', label: 'Top horizontal' },
    { value: 'bottom horizontal line', label: 'Bottom horizontal' }
  ]), []);
  const ruleOfThirdsFocusLabelMap = useMemo(() => ruleOfThirdsFocusOptions.reduce((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {}), [ruleOfThirdsFocusOptions]);
  const ruleOfThirdsGridCells = useMemo(() => ([
    { value: 'center punch', row: 1, col: 1, label: 'Center' },
    { value: 'top-left intersection', row: 0, col: 0, label: 'Top left' },
    { value: 'top-right intersection', row: 0, col: 2, label: 'Top right' },
    { value: 'bottom-left intersection', row: 2, col: 0, label: 'Bottom left' },
    { value: 'bottom-right intersection', row: 2, col: 2, label: 'Bottom right' },
    { value: 'left vertical line', row: 1, col: 0, label: 'Left vertical' },
    { value: 'right vertical line', row: 1, col: 2, label: 'Right vertical' },
    { value: 'top horizontal line', row: 0, col: 1, label: 'Top horizontal' },
    { value: 'bottom horizontal line', row: 2, col: 1, label: 'Bottom horizontal' }
  ]), []);

  const aiLensOptions = useMemo(() => ([
    '', '18mm','24mm','28mm','35mm','50mm','65mm','85mm'
  ]), []);

  const aiAngleOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'High angle', label: 'High' },
    { value: 'Low angle', label: 'Low' },
    { value: "Bird's eye view", label: "Bird's eye" },
    { value: 'Eye level', label: 'Eye level' },
    { value: 'Dutch angle', label: 'Dutch' },
  ]), []);

  const aiAspectOptions = useMemo(() => ([
    { value: '', label: 'Default' },
    { value: '1.33', label: '4:3 (1.33:1)' },
    { value: '1.66', label: '1.66:1' },
    { value: '1.78', label: '16:9 (1.78:1)' },
    { value: '1.85', label: '1.85:1' },
    { value: '2.39', label: '2.39:1 Scope' },
  ]), []);

  const aiTimeOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Dawn', label: 'Dawn' },
    { value: 'Sunrise', label: 'Sunrise' },
    { value: 'Morning', label: 'Morning' },
    { value: 'Noon', label: 'Noon' },
    { value: 'Afternoon', label: 'Afternoon' },
    { value: 'Golden hour', label: 'Golden hour' },
    { value: 'Sunset', label: 'Sunset' },
    { value: 'Blue hour', label: 'Blue hour' },
    { value: 'Night', label: 'Night' },
  ]), []);

  const aiAtmosphereOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Soft mist', label: 'Soft mist' },
    { value: 'Thick fog', label: 'Thick fog' },
    { value: 'Haze', label: 'Haze' },
    { value: 'Volumetric light beams', label: 'Volumetric light beams' },
    { value: 'Dust in air', label: 'Dust in air' },
    { value: 'Smoke', label: 'Smoke' },
  ]), []);

  const aiLightingOptions = useMemo(() => ([
    { label: 'None', value: '' },
    { label: 'Cinematic soft light', value: 'Cinematic soft light, diffused key, gentle contrast' },
    { value: 'Rembrandt lighting with soft key and triangle fill', label: 'Rembrandt' },
    { value: 'Low key, chiaroscuro contrast', label: 'Low key' },
    { value: 'High key soft diffused light', label: 'High key' },
    { value: 'Backlit rim light separating subject', label: 'Backlight' },
  ]), []);

  const aiCompositionOptions = useMemo(() => ([
    { value: '', label: 'None' },
    { value: 'Rule of thirds framing with negative space', label: 'Rule of thirds' },
    { value: 'Leading lines driving eye to subject', label: 'Leading lines' },
    { value: 'Symmetrical centered composition', label: 'Symmetry' },
    { value: 'Off-center with strong silhouette', label: 'Silhouette' },
  ]), []);

  const buildAiPrompt = useCallback(() => {
    const parts = [];
    if (aiImagePrompt.trim()) parts.push(aiImagePrompt.trim());
    if (aiShotSize) parts.push(`Shot: ${aiShotSize}`);
    if (aiLens) parts.push(`Lens: ${aiLens}`);
    if (aiAngle) parts.push(`Angle: ${aiAngle}`);
    if (aiAspectRatio) parts.push(`Aspect ratio: ${aiAspectRatio}`);
    if (aiTimeOfDay) {
      parts.push(`Time of day: ${aiTimeOfDay}`);
      if (aiTimeOfDay.toLowerCase() === 'night') {
        parts.push('Scene is fully night-time: dark sky, no sunset or dusk colors, no twilight glow.');
      }
    }
    if (aiAtmosphere) parts.push(`Atmosphere: ${aiAtmosphere}`);
    if (aiLighting) parts.push(`Lighting: ${aiLighting}`);
    if (aiComposition) parts.push(`Composition: ${aiComposition}`);
    if (aiComposition?.toLowerCase().includes('rule of thirds') && aiCompositionFocus) {
      const focusLabel = ruleOfThirdsFocusLabelMap[aiCompositionFocus] || aiCompositionFocus;
      parts.push(`Place the main subject toward the ${focusLabel.toLowerCase()} third of the frame, without any grid lines or overlays.`);
    }
    return parts.join('\n');
  }, [aiImagePrompt, aiShotSize, aiLens, aiAngle, aiAspectRatio, aiTimeOfDay, aiAtmosphere, aiLighting, aiComposition, aiCompositionFocus, ruleOfThirdsFocusLabelMap]);

  const resolveAspectDimensions = useCallback(() => {
    if (!aiAspectRatio) return null;
    const ratio = Number(aiAspectRatio);
    if (!Number.isFinite(ratio) || ratio <= 0) return null;
    const base = 1024; // keep close to default but respect ratio
    let width = base;
    let height = Math.round(base / ratio);
    const align = (val) => Math.max(512, Math.round(val / 8) * 8); // multiples of 8 for safety
    width = align(width);
    height = align(height);
    return { width, height };
  }, [aiAspectRatio]);
  const handleAiImageGenerate = async () => {
    const finalPrompt = buildAiPrompt();
    if (!finalPrompt.trim()) return;
    setGeneratingAiImage(true);
    try {
      // Reuse storyboard z-image workflow for generation
      const workflowOverrides = { ...DEFAULT_Z_IMAGE_WORKFLOW };
      const seedNum = Math.floor(Math.random() * 10_000_000);
      const dims = resolveAspectDimensions();
      const overrides = {
        '58': { value: finalPrompt },
        '57:3': { seed: seedNum },
        ...(dims
          ? {
              '57:13': {
                width: dims.width,
                height: dims.height,
                batch_size: 1
              }
            }
          : {})
      };
      const imageBase64 = await comfyService.runWorkflow(workflowOverrides, overrides, {
        pollIntervalMs: 1200,
        pollTimeoutMs: 120_000
      });
      const src = imageBase64?.startsWith('data:')
        ? imageBase64
        : `data:image/png;base64,${imageBase64}`;
      const newElement = createImageElement(src);
      const elements = [...(slide?.elements ?? []), newElement];
      replaceSlideElements(elements);
      setSelectedElementId(newElement.id);
      setAiImageDialogOpen(false);
    } catch (error) {
      console.error('AI image generation error:', error);
    } finally {
      setGeneratingAiImage(false);
    }
  };

  const handleDeleteElement = (elementId = selectedElementId) => {
    if (!elementId) return;
    const elements = slide?.elements ?? [];
    const targetIndex = elements.findIndex((element) => element.id === elementId);
    if (targetIndex < 0) return;

    const remaining = elements.filter((element) => element.id !== elementId);
    replaceSlideElements(remaining);

    setHoveredElementId((prev) => (prev === elementId ? null : prev));

    const selectedStillExists =
      selectedElementId && selectedElementId !== elementId &&
      remaining.some((element) => element.id === selectedElementId);

    if (selectedStillExists) {
      setSelectedElementId(selectedElementId);
      return;
    }

    const fallbackIndex = Math.min(targetIndex, remaining.length - 1);
    setSelectedElementId(fallbackIndex >= 0 ? remaining[fallbackIndex]?.id ?? null : null);
  };

  const handleOpenCopyMoveDialog = (mode) => {
    setCopyMoveMode(mode);
    setCopyMoveTargetIndex(currentSlideIndex);
    setCopyMoveDialogOpen(true);
  };

  const handleConfirmCopyMove = () => {
    if (!selectedElementId) return;
    const sourceIndex = currentSlideIndex;
    const targetIndex = copyMoveTargetIndex;
    if (targetIndex == null || targetIndex < 0 || targetIndex >= deck.slides.length) return;
    if (copyMoveMode === 'move' && targetIndex === sourceIndex) {
      setCopyMoveDialogOpen(false);
      return;
    }

    let newElementId = null;

    updateDeck((prev) => {
      const slides = [...prev.slides];
      const sourceSlide = { ...slides[sourceIndex] };
      const targetSlide = { ...slides[targetIndex] };
      const element = sourceSlide.elements.find((el) => el.id === selectedElementId);
      if (!element) return prev;

      const cloned = {
        ...element,
        id: createId(element.type || 'element')
      };

      newElementId = cloned.id;

      const sourceElements =
        copyMoveMode === 'move'
          ? sourceSlide.elements.filter((el) => el.id !== selectedElementId)
          : sourceSlide.elements;

      const targetElements = [...targetSlide.elements, cloned];

      slides[sourceIndex] = { ...sourceSlide, elements: sourceElements };
      slides[targetIndex] = { ...targetSlide, elements: targetElements };

      return { ...prev, slides };
    });

    setCopyMoveDialogOpen(false);
    setCurrentSlideIndex(copyMoveTargetIndex);
    setSelectedElementId(newElementId);
  };

  const handleLayerChange = (direction) => {
    if (!selectedElementId) return;
    const elements = slide?.elements ?? [];
    const index = elements.findIndex((item) => item.id === selectedElementId);
    if (index < 0) return;

    const reordered = [...elements];
    const [target] = reordered.splice(index, 1);

    if (direction === 'forward') {
      reordered.splice(index + 1, 0, target);
    } else if (direction === 'backward') {
      reordered.splice(Math.max(0, index - 1), 0, target);
    } else if (direction === 'front') {
      reordered.push(target);
    } else if (direction === 'back') {
      reordered.unshift(target);
    }

    replaceSlideElements(reordered);
    setSelectedElementId(target.id);
  };

  const handleElementTextChange = (elementId, value) => {
    const target = slideElements.find((item) => item.id === elementId);
    if (target?.type !== 'text') return;
    
    // Auto-resize if autoFit is enabled
    if (target.autoFit !== false) {
      // Measure height based on current width to grow with content
      const measureDiv = document.createElement('div');
      measureDiv.style.position = 'absolute';
      measureDiv.style.visibility = 'hidden';
      measureDiv.style.whiteSpace = 'pre-wrap';
      measureDiv.style.wordBreak = 'break-word';
      measureDiv.style.fontFamily = target.style?.fontFamily || DEFAULT_TEXT_STYLE.fontFamily;
      measureDiv.style.fontSize = `${target.style?.fontSize || DEFAULT_TEXT_STYLE.fontSize}px`;
      measureDiv.style.fontWeight = target.style?.fontWeight || DEFAULT_TEXT_STYLE.fontWeight;
      measureDiv.style.fontStyle = target.style?.fontStyle || DEFAULT_TEXT_STYLE.fontStyle;
      measureDiv.style.lineHeight = target.style?.lineHeight || DEFAULT_TEXT_STYLE.lineHeight;
      measureDiv.style.letterSpacing = `${target.style?.letterSpacing || DEFAULT_TEXT_STYLE.letterSpacing}px`;
      measureDiv.style.padding = '4px 8px'; // Match TextField padding
      const currentWidth = Number.isFinite(target.width) ? target.width : SLIDE_CANVAS.width * 0.6;
      measureDiv.style.width = `${currentWidth}px`;
      measureDiv.textContent = value || 'A'; // Minimum height for empty text
      
      document.body.appendChild(measureDiv);
      const height = Math.ceil(measureDiv.scrollHeight) + 10;
      document.body.removeChild(measureDiv);
      
      updateSlideElements(elementId, (element) => ({
        ...element,
        text: value,
        height: height
      }));
    } else {
      updateSlideElements(elementId, (element) => ({ ...element, text: value }));
    }
  };

  const renderElement = (element, options = {}) => {
    const { isPreview = false } = options;
    const selected = !isPreview && selectedElementId === element.id;

    if (element.type === 'image') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            border: selected ? '2px solid' : '1px solid',
            borderColor: selected ? 'primary.main' : 'transparent',
            backgroundColor: 'transparent'
          }}
        >
          {element.src && (
            <Box
              component="img"
              src={element.src}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: element.objectFit }}
            />
          )}
        </Box>
      );
    }

    if (element.type === 'shape') {
      const appliedShapeStyle = {
        ...DEFAULT_SHAPE_STYLE,
        ...(element.style ?? {})
      };
      
      // Check if this is an image placeholder
      const isPlaceholder = element.isImagePlaceholder;
      const hasBackgroundImage = element.backgroundImage;
      
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: `${appliedShapeStyle.borderRadius}px`,
            backgroundColor: hasBackgroundImage ? 'transparent' : appliedShapeStyle.fill,
            border: `${appliedShapeStyle.borderWidth}px ${appliedShapeStyle.borderStyle} ${appliedShapeStyle.borderColor}`,
            opacity: appliedShapeStyle.opacity,
            transition: 'all 0.2s ease',
            outline: selected ? '2px solid rgba(25,118,210,0.3)' : 'none',
            cursor: isPlaceholder ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: hasBackgroundImage ? `url(${element.backgroundImage})` : 'none',
            backgroundSize: hasBackgroundImage && element.imageScale 
              ? `${element.imageScale}%` 
              : (element.objectFit || 'cover'),
            backgroundPosition: hasBackgroundImage && element.imagePositionX !== undefined && element.imagePositionY !== undefined
              ? `${element.imagePositionX}% ${element.imagePositionY}%`
              : 'center',
            backgroundRepeat: 'no-repeat',
            '&:hover': isPlaceholder ? {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderColor: theme.palette.primary.main
            } : {}
          }}
          onClick={(e) => {
            if (isPlaceholder && !isPreview) {
              e.stopPropagation();
              handleImagePlaceholderClick(element.id);
            }
          }}
        >
          {isPlaceholder && !isPreview && !hasBackgroundImage && (
            <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              <ImageIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Click to upload image
              </Typography>
            </Stack>
          )}
        </Box>
      );
    }

    const appliedStyle = {
      ...DEFAULT_TEXT_STYLE,
      ...(element.style ?? {})
    };
    const alignment = element.align ?? 'left';

    const renderTextElement = (element, { isPreview, selected, appliedStyle, alignment }) => {
      const isRich = element.isRich;
      const isEditing = editingElementId === element.id;

      const displayBox = (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            px: 1,
            py: 0.5,
            fontFamily: appliedStyle.fontFamily,
            fontSize: `${appliedStyle.fontSize}px`,
            fontWeight: appliedStyle.fontWeight,
            fontStyle: appliedStyle.fontStyle,
            textDecoration: appliedStyle.textDecoration,
            color: appliedStyle.color,
            backgroundColor:
              appliedStyle.backgroundColor === 'transparent'
                ? 'transparent'
                : appliedStyle.backgroundColor,
            textAlign: alignment,
            lineHeight: appliedStyle.lineHeight,
            letterSpacing: `${appliedStyle.letterSpacing}px`,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            pointerEvents: isPreview ? 'none' : 'auto'
          }}
        >
          {element.text}
        </Box>
      );

      const hitbox = (
        <Box
          sx={{ position: 'absolute', inset: 0, pointerEvents: isEditing ? 'none' : 'auto' }}
          onMouseDown={(e) => handleElementMouseDown(e, element)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setSelectedElementId(element.id);
            setEditingElementId(element.id);
          }}
        />
      );

      if (!isPreview) {
        return displayBox;
      }

      if (!isEditing) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {displayBox}
            {hitbox}
          </Box>
        );
      }

      return (
        <TextField
          value={element.text}
          inputRef={(node) => {
            if (node) {
              textInputRefs.current[element.id] = node;
            } else {
              delete textInputRefs.current[element.id];
            }
          }}
          onChange={(e) => handleElementTextChange(element.id, e.target.value)}
          multiline
          variant="standard"
          placeholder="Click to add text"
          InputProps={{
            readOnly: false,
            disableUnderline: true,
            sx: {
              display: 'flex',
              alignItems: 'stretch',
              '& textarea': {
                fontFamily: appliedStyle.fontFamily,
                fontSize: `${appliedStyle.fontSize}px`,
                fontWeight: appliedStyle.fontWeight,
                fontStyle: appliedStyle.fontStyle,
                textDecoration: appliedStyle.textDecoration,
                color: appliedStyle.color,
                backgroundColor:
                  appliedStyle.backgroundColor === 'transparent'
                    ? 'transparent'
                    : appliedStyle.backgroundColor,
                textAlign: alignment,
                height: '100%',
                lineHeight: appliedStyle.lineHeight,
                letterSpacing: `${appliedStyle.letterSpacing}px`,
                resize: 'none'
              }
            },
            onBlur: () => {
              if (editingElementId === element.id) {
                setEditingElementId(null);
              }
            },
            onMouseDown: (event) => {
              if (isPreview) {
                event.stopPropagation();
                setSelectedElementId(element.id);
              }
            }
          }}
          sx={{
            width: '100%',
            height: '100%',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: selected ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
            pointerEvents: 'auto'
          }}
        />
      );
    };

    return renderTextElement(element, { isPreview, selected, appliedStyle, alignment });
  };

  const handleElementMouseDown = (event, element) => {
    event.stopPropagation();
    suppressDeselectRef.current = true;
    setTimeout(() => {
      suppressDeselectRef.current = false;
    }, 150);
    if (element.type === 'text' && event.target?.closest('textarea')) {
      setSelectedElementId(element.id);
      setEditingElementId(element.id);
      return;
    }

    setSelectedElementId(element.id);
    setEditingElementId(null);

    const dragStarted = { current: false };
    const startX = event.clientX;
    const startY = event.clientY;
    const resolveSize = (val, fallback) => (typeof val === 'number' && Number.isFinite(val) ? val : fallback);
    const initialWidth = resolveSize(element.width, 200);
    const initialHeight = resolveSize(element.height, 120);
    const initial = { x: element.x, y: element.y, width: initialWidth, height: initialHeight };

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

       if (!dragStarted.current) {
         if (Math.abs(dx) < 3 && Math.abs(dy) < 3) {
           return;
         }
         dragStarted.current = true;
         setIsDragging(true);
       }

      updateSlideElements(element.id, (prev) => {
        const prevWidth = resolveSize(prev.width, initialWidth);
        const prevHeight = resolveSize(prev.height, initialHeight);

        const rawX = Math.min(Math.max(0, initial.x + dx), SLIDE_CANVAS.width - prevWidth);
        const rawY = Math.min(Math.max(0, initial.y + dy), SLIDE_CANVAS.height - prevHeight);

        const snapActive = snapEnabled && !moveEvent.altKey;

        let nextX = snapActive ? Math.round(rawX / SNAP) * SNAP : rawX;
        let nextY = snapActive ? Math.round(rawY / SNAP) * SNAP : rawY;

        // Snap to canvas center if close
        const centerX = SLIDE_CANVAS.width / 2;
        const centerY = SLIDE_CANVAS.height / 2;
        const elementCenterX = rawX + prevWidth / 2;
        const elementCenterY = rawY + prevHeight / 2;

        if (snapActive && Math.abs(elementCenterX - centerX) < SNAP * 2) {
          nextX = centerX - prevWidth / 2;
        }
        if (snapActive && Math.abs(elementCenterY - centerY) < SNAP * 2) {
          nextY = centerY - prevHeight / 2;
        }

        if (snapActive) {
          nextX = Math.round(nextX / SNAP) * SNAP;
          nextY = Math.round(nextY / SNAP) * SNAP;
        }

        return { ...prev, x: nextX, y: nextY, width: prevWidth, height: prevHeight };
      });
    };

    const onMouseUp = () => {
      if (dragStarted.current) {
        setIsDragging(false);
      } else {
        // Treat as click: just select. Editing is triggered by textarea or double-click.
        setSelectedElementId(element.id);
        if (element.type !== 'text') {
          setEditingElementId(null);
        }
      }
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleCanvasDeselect = useCallback(() => {
    if (suppressDeselectRef.current) return;
    manualDeselectRef.current = true;
    setSelectedElementId(null);
    setEditingElementId(null);
  }, []);

  const handleResizeMouseDown = (event, element, corner) => {
    event.stopPropagation();
    setSelectedElementId(element.id);
    setIsResizing(true);
    const startX = event.clientX;
    const startY = event.clientY;
    const initial = {
      x: element.x,
      y: element.y,
      width: element.width,
      height: element.height,
      ratio: element.originalRatio || (element.width && element.height ? element.width / element.height : null),
      lock: element.lockAspect ?? lockImageAspect
    };

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      updateSlideElements(element.id, (prev) => {
        let nextX = prev.x;
        let nextY = prev.y;
        let nextWidth = prev.width;
        let nextHeight = prev.height;
        const aspect = initial.ratio;

        const snapActive = snapEnabled && !moveEvent.altKey;

        if (corner.includes('right')) {
          nextWidth = Math.max(80, initial.width + dx);
          nextWidth = Math.min(nextWidth, SLIDE_CANVAS.width - prev.x);
          if (initial.lock && aspect) {
            nextHeight = nextWidth / aspect;
          }
        }

        if (corner.includes('bottom')) {
          nextHeight = Math.max(60, initial.height + dy);
          nextHeight = Math.min(nextHeight, SLIDE_CANVAS.height - prev.y);
          if (initial.lock && aspect) {
            nextWidth = nextHeight * aspect;
          }
        }

        if (corner.includes('left')) {
          const maxLeft = initial.x + initial.width - 80;
          nextX = Math.min(Math.max(0, initial.x + dx), maxLeft);
          nextWidth = initial.width + (initial.x - nextX);
          if (initial.lock && aspect) {
            nextHeight = nextWidth / aspect;
          }
        }

        if (corner.includes('top')) {
          const maxTop = initial.y + initial.height - 60;
          nextY = Math.min(Math.max(0, initial.y + dy), maxTop);
          nextHeight = initial.height + (initial.y - nextY);
          if (initial.lock && aspect) {
            nextWidth = nextHeight * aspect;
          }
        }

        nextWidth = Math.min(nextWidth, SLIDE_CANVAS.width - nextX);
        nextHeight = Math.min(nextHeight, SLIDE_CANVAS.height - nextY);

        if (snapActive) {
          nextX = Math.round(nextX / SNAP) * SNAP;
          nextY = Math.round(nextY / SNAP) * SNAP;
          nextWidth = Math.round(nextWidth / SNAP) * SNAP;
          nextHeight = Math.round(nextHeight / SNAP) * SNAP;
        }

        nextWidth = Math.max(80, Math.min(nextWidth, SLIDE_CANVAS.width - nextX));
        nextHeight = Math.max(60, Math.min(nextHeight, SLIDE_CANVAS.height - nextY));

        return {
          ...prev,
          x: nextX,
          y: nextY,
          width: nextWidth,
          height: nextHeight
        };
      });
    };

    const onMouseUp = () => {
      setIsResizing(false);
      setEditingElementId(null);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const renderResizeHandle = (element, corner) => {
    const positionStyles = {
      top: corner.includes('top') ? -6 : 'auto',
      bottom: corner.includes('bottom') ? -6 : 'auto',
      left: corner.includes('left') ? -6 : 'auto',
      right: corner.includes('right') ? -6 : 'auto'
    };

    return (
      <Box
        key={corner}
        onMouseDown={(event) => handleResizeMouseDown(event, element, corner)}
        sx={{
          position: 'absolute',
          width: 14,
          height: 14,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          border: '2px solid white',
          boxShadow: '0 2px 6px rgba(37, 99, 235, 0.4)',
          transition: 'all 0.2s ease',
          cursor:
            corner === 'top-left'
              ? 'nwse-resize'
              : corner === 'top-right'
              ? 'nesw-resize'
              : corner === 'bottom-left'
              ? 'nesw-resize'
              : 'nwse-resize',
          '&:hover': {
            transform: 'scale(1.3)',
            boxShadow: '0 3px 10px rgba(37, 99, 235, 0.5)'
          },
          ...positionStyles
        }}
      />
    );
  };

  const renderSlideCanvas = (targetSlide = slide, { preview = true } = {}) => {
    const elements = targetSlide?.elements ?? [];
    const backgroundColor = targetSlide?.backgroundColor ?? '#ffffff';

    return (
      <Paper
        className="pitchdeck-print-slide"
        elevation={0}
        sx={{
          position: 'relative',
          width: SLIDE_CANVAS.width,
          height: SLIDE_CANVAS.height,
          maxWidth: '100%',
          backgroundColor,
          borderRadius: 0,
          overflow: 'hidden',
          border: preview ? '2px solid' : '1px solid',
          borderColor: preview ? alpha(theme.palette.primary.main, 0.2) : 'divider',
          transition: 'all 0.3s ease'
        }}
        onClick={preview ? handleCanvasDeselect : undefined}
      >
        {elements.map((element) => {
          const selected = preview && selectedElementId === element.id;
          return (
            <Box
              key={element.id}
              onMouseDown={preview ? (event) => handleElementMouseDown(event, element) : undefined}
              onMouseEnter={preview ? () => setHoveredElementId(element.id) : undefined}
              onMouseLeave={preview ? () => setHoveredElementId(null) : undefined}
              onClick={preview ? (event) => {
                event.stopPropagation();
                setSelectedElementId(element.id);
              } : undefined}
              sx={{
                position: 'absolute',
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                border: selected ? '1px solid rgba(25,118,210,0.6)' : '1px solid transparent',
                borderRadius: 1,
                backgroundColor: 'transparent',
                cursor: preview ? (selected ? 'move' : 'pointer') : 'default'
              }}
              tabIndex={preview ? 0 : -1}
              onFocus={preview ? () => setSelectedElementId(element.id) : undefined}
              onDoubleClick={preview ? () => setEditingElementId(element.id) : undefined}
            >
              {renderElement(element, { isPreview: preview })}
              {preview && hoveredElementId === element.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 10
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Tooltip title="Delete element">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteElement(element.id);
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: 'transparent',
                        color: theme.palette.error.main,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                          borderColor: theme.palette.error.main
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {preview && selected && (
                <>
                  {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) =>
                    renderResizeHandle(element, corner)
                  )}
                </>
              )}
            </Box>
          );
        })}
        {preview && isDragging && showGuides && (
          <>
            {/* Vertical center guide */}
            <Box
              sx={{
                position: 'absolute',
                left: SLIDE_CANVAS.width / 2,
                top: 0,
                width: 1,
                height: SLIDE_CANVAS.height,
                bgcolor: 'primary.main',
                opacity: 0.5,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            />
            {/* Horizontal center guide */}
            <Box
              sx={{
                position: 'absolute',
                top: SLIDE_CANVAS.height / 2,
                left: 0,
                width: SLIDE_CANVAS.width,
                height: 1,
                bgcolor: 'primary.main',
                opacity: 0.5,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            />
          </>
        )}
      </Paper>
    );
  };

  const renderThumbnail = (item, index) => {
    const definition = templateDefinitions[index] ?? templateDefinitions[0];
    const primaryText = item.elements?.[0]?.text || definition.label;
    const secondaryText = item.elements?.[1]?.text || definition.placeholder;

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.9rem',
            fontWeight: 700,
            bgcolor: index === currentSlideIndex 
              ? 'primary.main' 
              : alpha(theme.palette.text.primary, 0.08),
            color: index === currentSlideIndex ? 'primary.contrastText' : 'text.secondary',
            boxShadow: index === currentSlideIndex
              ? '0 2px 8px rgba(37, 99, 235, 0.3)'
              : 'none',
            transition: 'all 0.25s ease'
          }}
        >
          {index + 1}
        </Avatar>
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="subtitle2" 
            noWrap 
            sx={{ 
              fontWeight: 700,
              fontSize: '0.875rem',
              color: index === currentSlideIndex ? 'primary.main' : 'text.primary'
            }}
          >
            {primaryText}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            noWrap
            sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}
          >
            {secondaryText}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      p: 2, 
      gap: 2,
      backgroundColor: theme.palette.mode === 'dark' 
        ? '#0a0a0a'
        : '#fff'
    }}>
      <input
        type="file"
        accept="image/*"
        hidden
        ref={fileInputRef}
        onChange={handleImageSelected}
      />
      <GlobalStyles
        styles={{
          '.pitchdeck-print-area': {
            display: 'none'
          },
          '@page': {
            margin: '12mm',
            size: 'landscape'
          },
          '@media print': {
            body: {
              backgroundColor: '#fff',
              margin: 0
            },
            'body *': {
              visibility: 'hidden'
            },
            '.pitchdeck-print-hidden': {
              display: 'none !important'
            },
            '.pitchdeck-print-slide': {
              boxShadow: 'none !important',
              border: '1px solid #cbd5f5 !important',
              width: `${SLIDE_CANVAS.width}px !important`,
              height: `${SLIDE_CANVAS.height}px !important`,
              margin: '0 auto 16mm !important',
              breakInside: 'avoid',
              pageBreakInside: 'avoid'
            },
            '.pitchdeck-print-area, .pitchdeck-print-area *': {
              visibility: 'visible'
            },
            '.pitchdeck-print-area': {
              display: 'flex',
              position: 'absolute',
              inset: 0,
              width: '100%',
              minHeight: '100%',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16mm',
              padding: 0,
              boxSizing: 'border-box'
            }
          }
        }}
      />
      <Paper 
        className="pitchdeck-print-hidden" 
        elevation={0}
        sx={{ 
          p: 2.25, 
          borderRadius: 1,
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha('#2a2a2a', 0.8)
            : '#fff',
          border: theme.palette.mode === 'dark'
            ? `1px solid ${alpha(theme.palette.divider, 0.3)}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 16px rgba(0,0,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.06)',
          position: 'sticky',
          top: 0,
          zIndex: 5,
          mb: 0
        }}
      >
        <Stack spacing={isTextSelected ? 1.25 : 0.75}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main
              }}>
                <PdfIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Pitch Deck
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {saveStatus === 'saving' && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: theme.palette.warning.main,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    >
                      • Saving...
                    </Typography>
                  )}
                  {saveStatus === 'saved' && lastSaved && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: theme.palette.success.main,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    >
                      • Saved {lastSaved.toLocaleTimeString()}
                    </Typography>
                  )}
                  {saveStatus === 'error' && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: theme.palette.error.main,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    >
                      • Saved locally
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
            <TextField
              label="Logline"
              value={deck.logline}
              onChange={(e) => handleLoglineChange(e.target.value)}
              multiline
              minRows={2}
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiInputBase-input': { 
                  fontStyle: deck.logline ? 'normal' : 'italic',
                  fontSize: '0.95rem',
                  lineHeight: 1.6
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)'
                  }
                }
              }}
            />
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} alignItems="flex-start">
            {/* Column 1: Add Elements */}
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: 'text.secondary', 
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                Add Elements
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Add text box">
                  <IconButton onClick={handleAddText} sx={toolbarIconSx}>
                    <TextIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add shape">
                  <IconButton onClick={handleAddShape} sx={toolbarIconSx}>
                    <ShapeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add image">
                  <IconButton onClick={handleImageRequest} sx={toolbarIconSx}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate AI image">
                  <IconButton onClick={handleAiImageRequest} sx={toolbarIconSx}>
                    <AiImageIcon />
                  </IconButton>
                </Tooltip>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={lockImageAspect}
                      onChange={(e) => setLockImageAspect(e.target.checked)}
                    />
                  }
                  label="Lock aspect"
                  sx={{ ml: 1 }}
                />
              </Stack>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Column 1b: Selected Image */}
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: 'text.secondary', 
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                Image
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Copy to slide">
                  <span>
                    <IconButton
                      onClick={() => handleOpenCopyMoveDialog('copy')}
                      sx={toolbarIconSx}
                      disabled={!isImageSelected}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move to slide">
                  <span>
                    <IconButton
                      onClick={() => handleOpenCopyMoveDialog('move')}
                      sx={toolbarIconSx}
                      disabled={!isImageSelected}
                    >
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Export selected image">
                  <span>
                    <IconButton
                      onClick={handleExportSelectedImage}
                      sx={toolbarIconSx}
                      disabled={!isImageSelected}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Column 2: Layers */}
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
                LAYERS
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Bring forward">
                  <span>
                    <IconButton
                      sx={layerButtonSx}
                      onClick={() => handleLayerChange('forward')}
                      disabled={!isElementSelected || isAtFront}
                    >
                      <MoveUpIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Send backward">
                  <span>
                    <IconButton
                      sx={layerButtonSx}
                      onClick={() => handleLayerChange('backward')}
                      disabled={!isElementSelected || isAtBack}
                    >
                      <MoveDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Bring to front">
                  <span>
                    <IconButton
                      sx={layerButtonSx}
                      onClick={() => handleLayerChange('front')}
                      disabled={!isElementSelected || isAtFront}
                    >
                      <BringToFrontIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Send to back">
                  <span>
                    <IconButton
                      sx={layerButtonSx}
                      onClick={() => handleLayerChange('back')}
                      disabled={!isElementSelected || isAtBack}
                    >
                      <SendToBackIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Divider orientation="vertical" flexItem />

            {/* Column 3: Slide Settings */}
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
                SLIDE
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                <Tooltip title="Slide background color">
                  <span>
                    <IconButton
                      onClick={() => slideBackgroundInputRef.current?.click()}
                      sx={toolbarIconSx}
                    >
                      <PaletteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box
                  component="input"
                  type="color"
                  ref={slideBackgroundInputRef}
                  value={slideBackgroundColor}
                  onChange={(event) => handleSlideBackgroundChange(event.target.value)}
                  sx={{ display: 'none' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={snapEnabled}
                      onChange={(e) => setSnapEnabled(e.target.checked)}
                    />
                  }
                  label="Snap"
                  sx={{ ml: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showGuides}
                      onChange={(e) => setShowGuides(e.target.checked)}
                    />
                  }
                  label="Guides"
                  sx={{ ml: 1 }}
                />
                <FormControl size="small" sx={{ 
                  minWidth: 110,
                  '& .MuiInputBase-root': { fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                }}>
                  <InputLabel id="pitch-template-select-label">Template</InputLabel>
                  <Select
                    labelId="pitch-template-select-label"
                    id="pitch-template-select"
                    label="Template"
                    value={deck.template ?? DEFAULT_TEMPLATE_KEY}
                    onChange={(event) => handleTemplateChange(event.target.value)}
                  >
                    {Object.entries(TEMPLATE_CONFIGS).map(([key, config]) => (
                      <MenuItem 
                        key={key} 
                        value={key}
                        sx={{
                          fontSize: '0.875rem',
                          color: theme.palette.text.primary,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                          },
                          '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.16)
                            }
                          }
                        }}
                      >
                        {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Choose Theme">
                  <IconButton onClick={() => setThemeDialogOpen(true)} sx={toolbarIconSx}>
                    <ThemeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Change Layout">
                  <IconButton onClick={() => setLayoutDialogOpen(true)} sx={toolbarIconSx}>
                    <LayoutIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Deck">
                  <IconButton onClick={handleResetDeck} sx={toolbarIconSx}>
                    <ResetIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export PDF">
                  <IconButton onClick={handleExportPDF} sx={toolbarIconSx}>
                    <PdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print">
                  <IconButton onClick={handlePrintDeck} sx={toolbarIconSx}>
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>

          {isTextSelected && <Divider sx={{ my: 0.5 }} />}

          {isTextSelected && (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {/* Column 1: Font */}
              <Stack spacing={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  FONT
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <TextField
                    select
                    label="Family"
                    value={selectedTextStyle.fontFamily}
                    onChange={(e) => handleTextStyleChange('fontFamily', e.target.value)}
                    size="small"
                    sx={{ 
                      width: 130,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  >
                    {FONT_FAMILIES.map((family) => (
                      <MenuItem key={family} value={family}>
                        {family}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Size"
                    type="number"
                    value={selectedTextStyle.fontSize}
                    onChange={(e) =>
                      handleTextStyleChange('fontSize', Number(e.target.value) || DEFAULT_TEXT_STYLE.fontSize)
                    }
                    size="small"
                    sx={{ 
                      width: 70,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                    inputProps={{ min: 8, max: 200 }}
                  />
                </Stack>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Column 2: Format */}
              <Stack spacing={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  FORMAT
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <IconButton
                    aria-label="Bold"
                    onClick={() => handleTextStyleChange('fontWeight', isBold ? 500 : 700)}
                    sx={getFormatButtonSx(isBold)}
                  >
                    <BoldIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Italic"
                    onClick={() => handleTextStyleChange('fontStyle', isItalic ? 'normal' : 'italic')}
                    sx={getFormatButtonSx(isItalic)}
                  >
                    <ItalicIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Underline"
                    onClick={() =>
                      handleTextStyleChange('textDecoration', hasUnderline ? 'none' : 'underline')
                    }
                    sx={getFormatButtonSx(hasUnderline)}
                  >
                    <UnderlineIcon />
                  </IconButton>
                </Stack>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Column 3: Align */}
              <Stack spacing={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  ALIGN
                </Typography>
                <ToggleButtonGroup
                  value={selectedElement.align}
                  exclusive
                  onChange={handleAlignChange}
                  size="small"
                >
                  <ToggleButton value="left" aria-label="Align left">
                    <AlignLeftIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="center" aria-label="Align center">
                    <AlignCenterIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="right" aria-label="Align right">
                    <AlignRightIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Column 4: Colors */}
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  COLORS
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ minHeight: 32 }}>
                  <Tooltip title="Text color">
                    <Box
                      onClick={() => textColorInputRef.current?.click()}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'divider',
                        backgroundColor: selectedTextStyle.color,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          borderColor: 'primary.main'
                        }
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Text
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    ref={textColorInputRef}
                    value={selectedTextStyle.color}
                    onChange={(e) => handleTextStyleChange('color', e.target.value)}
                    sx={{ display: 'none' }}
                  />
                  <Tooltip title="Background color">
                    <Box
                      onClick={() => textBackgroundInputRef.current?.click()}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'divider',
                        backgroundColor: backgroundColorValue === 'transparent' ? '#fff' : backgroundColorValue,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundImage: backgroundColorValue === 'transparent' 
                          ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                          : 'none',
                        backgroundSize: '6px 6px',
                        backgroundPosition: '0 0, 3px 3px',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          borderColor: 'primary.main'
                        }
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    BG
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    ref={textBackgroundInputRef}
                    value={backgroundColorValue}
                    onChange={(e) => handleTextStyleChange('backgroundColor', e.target.value)}
                    sx={{ display: 'none' }}
                  />
                  <Button 
                    size="small" 
                    variant="text" 
                    onClick={() => handleTextStyleChange('backgroundColor', 'transparent')}
                    sx={{ 
                      fontSize: '0.7rem',
                      minWidth: 'auto',
                      px: 1,
                      py: 0.25
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          )}

          {isShapeSelected && <Divider sx={{ my: 0.5 }} />}

          {isShapeSelected && (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {/* Column 1: Colors */}
              <Stack spacing={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  COLORS
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 32 }}>
                  <Tooltip title="Fill color">
                    <Box
                      onClick={() => shapeFillInputRef.current?.click()}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'divider',
                        backgroundColor: selectedShapeStyle.fill,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          borderColor: 'primary.main'
                        }
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Fill
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    ref={shapeFillInputRef}
                    value={selectedShapeStyle.fill}
                    onChange={(e) =>
                      handleShapeStyleChange('fill', e.target.value || DEFAULT_SHAPE_STYLE.fill)
                    }
                    sx={{ display: 'none' }}
                  />
                  <Tooltip title="Border color">
                    <Box
                      onClick={() => shapeBorderInputRef.current?.click()}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        border: '3px solid',
                        borderColor: selectedShapeStyle.borderColor,
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Border
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    ref={shapeBorderInputRef}
                    value={selectedShapeStyle.borderColor}
                    onChange={(e) =>
                      handleShapeStyleChange('borderColor', e.target.value || DEFAULT_SHAPE_STYLE.borderColor)
                    }
                    sx={{ display: 'none' }}
                  />
                </Stack>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Column 2: Border Properties */}
              <Stack spacing={1}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  BORDER
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <TextField
                    label="Width"
                    type="number"
                    size="small"
                    value={selectedShapeStyle.borderWidth}
                    onChange={(e) =>
                      handleShapeStyleChange(
                        'borderWidth',
                        Number(e.target.value) || DEFAULT_SHAPE_STYLE.borderWidth
                      )
                    }
                    inputProps={{ min: 0, max: 20 }}
                    sx={{ 
                      width: 75,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                  <TextField
                    select
                    label="Style"
                    size="small"
                    value={selectedShapeStyle.borderStyle}
                    onChange={(e) =>
                      handleShapeStyleChange('borderStyle', e.target.value || DEFAULT_SHAPE_STYLE.borderStyle)
                    }
                    sx={{ 
                      width: 90,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  >
                    <MenuItem value="solid">Solid</MenuItem>
                    <MenuItem value="dashed">Dashed</MenuItem>
                    <MenuItem value="dotted">Dotted</MenuItem>
                    <MenuItem value="double">Double</MenuItem>
                  </TextField>
                  <TextField
                    label="Radius"
                    type="number"
                    size="small"
                    value={selectedShapeStyle.borderRadius}
                    onChange={(e) =>
                      handleShapeStyleChange(
                        'borderRadius',
                        Number(e.target.value) || DEFAULT_SHAPE_STYLE.borderRadius
                      )
                    }
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ 
                      width: 75,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                  <TextField
                    label="Opacity"
                    type="number"
                    size="small"
                    value={selectedShapeStyle.opacity}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const clamped = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : DEFAULT_SHAPE_STYLE.opacity;
                      handleShapeStyleChange('opacity', clamped);
                    }}
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    sx={{ 
                      width: 75,
                      '& .MuiInputBase-root': { fontSize: '0.875rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                    }}
                  />
                </Stack>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Column 3: Image Controls */}
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  IMAGE
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImageIcon fontSize="small" />}
                    onClick={handleShapeImageUpload}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontSize: '0.8125rem',
                      px: 1.5,
                      py: 0.5
                    }}
                  >
                    {selectedElement?.backgroundImage ? 'Change' : 'Add'}
                  </Button>
                  <Box
                    component="input"
                    type="file"
                    accept="image/*"
                    ref={shapeImageInputRef}
                    onChange={handleShapeImageSelected}
                    sx={{ display: 'none' }}
                  />
                  {selectedElement?.backgroundImage && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setModalPosition({ x: 0, y: 0 });
                          setImageAdjustDialogOpen(true);
                        }}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '0.8125rem',
                          px: 1.5,
                          py: 0.5
                        }}
                      >
                        Adjust
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() =>
                          updateSlideElements(selectedElementId, (element) => {
                            const { backgroundImage, objectFit, imageScale, imagePositionX, imagePositionY, ...rest } = element;
                            return rest;
                          })
                        }
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontSize: '0.8125rem',
                          px: 1.5,
                          py: 0.5
                        }}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}

        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, gap: 1.25, minHeight: 520, alignItems: 'stretch', mt: 0 }}>
        <Paper
          className="pitchdeck-print-hidden"
          elevation={0}
          sx={{
            width: 300,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            gap: 1.5,
            borderRadius: 0,
            border: '1px solid',
            borderColor: 'divider',
            background: theme.palette.background.paper,
            position: 'sticky',
            top: theme.spacing(2) + 64,
            height: 'calc(100vh - 120px)',
            overflow: 'hidden'
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'text.primary',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.75rem'
              }}
            >
              Slides
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Add slide">
                <span>
                  <IconButton size="small" onClick={handleAddSlideOpen}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete slide">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDeleteSlide}
                    disabled={deck.slides.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => handleMoveSlide('up')}
                disabled={currentSlideIndex === 0}
              >
                <MoveUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleMoveSlide('down')}
                disabled={currentSlideIndex >= deck.slides.length - 1}
              >
                <MoveDownIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ mx: -2 }} />
          <List
            sx={{
              flex: 1,
              overflowY: 'auto',
              py: 0,
              pr: 1,
              '&::-webkit-scrollbar': {
                width: 6
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.text.primary, 0.3),
                borderRadius: 3
              }
            }}
          >
            {deck.slides.map((item, index) => (
              <ListItemButton
                key={item.id}
                selected={currentSlideIndex === index}
                onClick={() => setCurrentSlideIndex(index)}
                sx={{
                  borderRadius: 0,
                  mb: 1,
                  p: 1.5,
                  border: dragOverIndex === index ? '2px dashed' : '1px solid',
                  borderColor: dragOverIndex === index ? 'primary.main' : 'divider',
                  cursor: 'grab',
                  bgcolor: currentSlideIndex === index 
                    ? (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.06))
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.action.hover, 0.04),
                    transform: 'translateX(4px)'
                  }
                }}
                draggable
                onDragStart={(event) => handleSlideDragStart(event, index)}
                onDragEnter={(event) => handleSlideDragEnter(event, index)}
                onDragOver={handleSlideDragOver}
                onDragEnd={handleSlideDragEnd}
                onDrop={(event) => handleSlideDrop(event, index)}
              >
                {renderThumbnail(item, index)}
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Menu anchorEl={addSlideAnchorEl} open={Boolean(addSlideAnchorEl)} onClose={handleAddSlideClose}>
          {templateDefinitions
            .filter((definition) => !definition.isTitle || deck.slides.length === 0)
            .map((definition) => {
              const getSlideIcon = (type) => {
                switch (type) {
                  case 'moodBoard': return <MoodBoardIcon fontSize="small" />;
                  case 'budgetBreakdown': return <BudgetIcon fontSize="small" />;
                  case 'timeline': return <TimelineIcon fontSize="small" />;
                  case 'comparables': return <ComparablesIcon fontSize="small" />;
                  default: return null;
                }
              };
              
              return (
                <MenuItem 
                  key={definition.type} 
                  onClick={() => handleAddSlide(definition.type)}
                  sx={{ gap: 1.5 }}
                >
                  {getSlideIcon(definition.type)}
                  {definition.label}
                </MenuItem>
              );
            })}
          <MenuItem onClick={handleAddCustomSlide} sx={{ gap: 1.5 }}>
            <AddIcon fontSize="small" />
            Custom Slide
          </MenuItem>
        </Menu>

        <Dialog open={copyMoveDialogOpen} onClose={() => setCopyMoveDialogOpen(false)}>
          <DialogTitle>{copyMoveMode === 'move' ? 'Move Image to Slide' : 'Copy Image to Slide'}</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="copy-move-slide-select">Target slide</InputLabel>
              <Select
                labelId="copy-move-slide-select"
                value={copyMoveTargetIndex}
                label="Target slide"
                onChange={(e) => setCopyMoveTargetIndex(Number(e.target.value))}
              >
                {deck.slides.map((slideItem, idx) => (
                  <MenuItem key={slideItem.id} value={idx}>
                    {`Slide ${idx + 1}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCopyMoveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmCopyMove} variant="contained">
              {copyMoveMode === 'move' ? 'Move' : 'Copy'}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          className="pitchdeck-print-hidden"
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'dark'
              ? '#1a1a1a'
              : '#f5f5f5',
            borderRadius: 0,
            border: theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
              : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            px: 3,
            py: 2.5
          }}
          onClick={handleCanvasDeselect}
        >
          <Box sx={{ 
            transform: `scale(${slideZoom})`,
            transformOrigin: 'center',
            transition: 'transform 0.2s ease'
          }}>
            {renderSlideCanvas()}
          </Box>
          
          {/* Zoom Controls Overlay */}
          <Box sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            display: 'flex',
            gap: 1,
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
            borderRadius: 0,
            padding: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }}>
            <Tooltip title="Zoom out">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideZoom(prev => Math.max(0.25, prev - 0.25));
                }}
                disabled={slideZoom <= 0.25}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>−</Typography>
              </IconButton>
            </Tooltip>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minWidth: 60,
              px: 1
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {Math.round(slideZoom * 100)}%
              </Typography>
            </Box>
            <Tooltip title="Zoom in">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideZoom(prev => Math.min(2, prev + 0.25));
                }}
                disabled={slideZoom >= 2}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>+</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset to 150%">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSlideZoom(1.5);
                }}
              >
                <ResetIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Dialog open={layoutDialogOpen} onClose={() => setLayoutDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose Page Layout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a layout template for the current slide
          </Typography>
          <Stack spacing={2}>
            {Object.entries(PAGE_LAYOUTS).map(([key, layout]) => {
              const IconComponent = {
                SingleColumnIcon,
                TwoColumnIcon,
                ColumnLayoutIcon,
                ViewDay: SingleColumnIcon,
                GridLayoutIcon
              }[layout.icon] || LayoutIcon;

              return (
                <Paper
                  key={key}
                  elevation={selectedLayout === key ? 4 : 1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: selectedLayout === key ? 'primary.main' : 'transparent',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 16px rgba(15, 23, 42, 0.12)'
                    }
                  }}
                  onClick={() => setSelectedLayout(key)}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 80,
                        height: 60,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {layout.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {layout.description}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLayoutDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => handleLayoutChange(selectedLayout)}
            variant="contained"
          >
            Apply Layout
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={themeDialogOpen} onClose={() => setThemeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Choose Pitch Deck Theme</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a professional theme to apply consistent styling across all slides
          </Typography>
          <Stack spacing={2}>
            {Object.entries(PITCH_DECK_THEMES).map(([key, theme]) => (
              <Paper
                key={key}
                elevation={selectedTheme === key ? 4 : 1}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selectedTheme === key ? 'primary.main' : 'transparent',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: 'primary.light',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(15, 23, 42, 0.12)'
                  }
                }}
                onClick={() => setSelectedTheme(key)}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 60,
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {theme.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {theme.description}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {Object.entries(theme.colors).slice(0, 5).map(([colorKey, colorValue]) => (
                        <Box
                          key={colorKey}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: colorValue,
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThemeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => handleThemeChange(selectedTheme)}
            variant="contained"
          >
            Apply Theme
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={aiImageDialogOpen} onClose={handleAiImageDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Generate AI Image</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image Prompt"
            fullWidth
            variant="outlined"
            value={aiImagePrompt}
            onChange={(e) => setAiImagePrompt(e.target.value)}
            placeholder="Describe the image you want to generate..."
            multiline
            rows={3}
          />
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Shot size</InputLabel>
                <Select value={aiShotSize} label="Shot size" onChange={(e) => setAiShotSize(e.target.value)}>
                  {aiShotOptions.map(opt => (
                    <MenuItem key={opt.label || 'none-shot'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Lens</InputLabel>
                <Select value={aiLens} label="Lens" onChange={(e) => setAiLens(e.target.value)}>
                  {aiLensOptions.map(val => (
                    <MenuItem key={val || 'none-lens'} value={val}>{val || 'None'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Angle</InputLabel>
                <Select value={aiAngle} label="Angle" onChange={(e) => setAiAngle(e.target.value)}>
                  {aiAngleOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Aspect ratio</InputLabel>
                <Select value={aiAspectRatio} label="Aspect ratio" onChange={(e) => setAiAspectRatio(e.target.value)}>
                  {aiAspectOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Time of day</InputLabel>
                <Select value={aiTimeOfDay} label="Time of day" onChange={(e) => setAiTimeOfDay(e.target.value)}>
                  {aiTimeOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Atmosphere</InputLabel>
                <Select value={aiAtmosphere} label="Atmosphere" onChange={(e) => setAiAtmosphere(e.target.value)}>
                  {aiAtmosphereOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Lighting</InputLabel>
                <Select value={aiLighting} label="Lighting" onChange={(e) => setAiLighting(e.target.value)}>
                  {aiLightingOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Composition</InputLabel>
                <Select value={aiComposition} label="Composition" onChange={(e) => setAiComposition(e.target.value)}>
                  {aiCompositionOptions.map(opt => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {aiComposition?.toLowerCase().includes('rule of thirds') && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                  Rule of thirds focus
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 320,
                    aspectRatio: '16 / 9',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(90deg, transparent 33.333%, rgba(255,255,255,0.35) 33.333%, rgba(255,255,255,0.35) 66.667%, transparent 66.667%), linear-gradient(0deg, transparent 33.333%, rgba(255,255,255,0.35) 33.333%, rgba(255,255,255,0.35) 66.667%, transparent 66.667%)',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(3, 1fr)',
                    gap: 0,
                    mt: 0.5
                  }}
                >
                  {ruleOfThirdsGridCells.map((cell) => {
                    const selected = aiCompositionFocus === cell.value;
                    return (
                      <Box
                        key={cell.value || cell.label}
                        onClick={() => setAiCompositionFocus(cell.value)}
                        sx={{
                          gridRow: cell.row + 1,
                          gridColumn: cell.col + 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: selected ? 'primary.contrastText' : 'text.primary',
                          bgcolor: selected ? 'primary.main' : 'transparent',
                          border: selected ? '2px solid' : '1px dashed',
                          borderColor: selected ? 'primary.main' : 'rgba(255,255,255,0.4)',
                          fontSize: 12,
                          fontWeight: 600,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            bgcolor: selected ? 'primary.dark' : 'rgba(0,0,0,0.04)',
                            borderColor: selected ? 'primary.dark' : 'rgba(0,0,0,0.2)'
                          }
                        }}
                      >
                        {cell.label}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAiImageDialogClose} disabled={generatingAiImage}>
            Cancel
          </Button>
          <Button
            onClick={handleAiImageGenerate}
            variant="contained"
            startIcon={generatingAiImage ? <CircularProgress color="inherit" size={18} /> : null}
            disabled={generatingAiImage}
          >
            {generatingAiImage ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={imageAdjustDialogOpen} 
        onClose={() => setImageAdjustDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            position: 'absolute',
            top: modalPosition.y,
            left: modalPosition.x,
            m: 0
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            cursor: 'move',
            userSelect: 'none',
            bgcolor: isDraggingModal ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
          onMouseDown={(e) => {
            setIsDraggingModal(true);
            const startX = e.clientX;
            const startY = e.clientY;
            const startPosX = modalPosition.x;
            const startPosY = modalPosition.y;

            const onMouseMove = (moveEvent) => {
              const deltaX = moveEvent.clientX - startX;
              const deltaY = moveEvent.clientY - startY;
              
              setModalPosition({
                x: startPosX + deltaX,
                y: startPosY + deltaY
              });
            };

            const onMouseUp = () => {
              setIsDraggingModal(false);
              window.removeEventListener('mousemove', onMouseMove);
              window.removeEventListener('mouseup', onMouseUp);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
          }}
        >
          <DragIcon sx={{ color: 'text.secondary', opacity: 0.5 }} />
          Adjust Image Position & Zoom
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Drag the image to reposition it, or use the zoom buttons below
            </Typography>
            
            {/* Image Preview Container */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                p: 3,
                backgroundColor: 'grey.50',
                borderRadius: 2
              }}
            >
              <Box
                ref={imageAdjustRef}
                sx={{
                  position: 'relative',
                  width: selectedElement?.width || 400,
                  height: selectedElement?.height || 300,
                  maxWidth: '100%',
                  maxHeight: 500,
                  border: '2px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: isDraggingImage ? 'grabbing' : 'grab',
                  backgroundColor: 'grey.100',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                onMouseDown={(e) => {
                  if (!selectedElement?.backgroundImage) return;
                  setIsDraggingImage(true);
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const startPosX = selectedElement.imagePositionX || 50;
                  const startPosY = selectedElement.imagePositionY || 50;

                  const onMouseMove = (moveEvent) => {
                    const container = imageAdjustRef.current;
                    if (!container) return;
                    
                    const rect = container.getBoundingClientRect();
                    const deltaX = ((moveEvent.clientX - startX) / rect.width) * 100;
                    const deltaY = ((moveEvent.clientY - startY) / rect.height) * 100;
                    
                    const newPosX = Math.max(0, Math.min(100, startPosX + deltaX));
                    const newPosY = Math.max(0, Math.min(100, startPosY + deltaY));
                    
                    updateSlideElements(selectedElementId, (element) => ({
                      ...element,
                      imagePositionX: newPosX,
                      imagePositionY: newPosY
                    }));
                  };

                  const onMouseUp = () => {
                    setIsDraggingImage(false);
                    window.removeEventListener('mousemove', onMouseMove);
                    window.removeEventListener('mouseup', onMouseUp);
                  };

                  window.addEventListener('mousemove', onMouseMove);
                  window.addEventListener('mouseup', onMouseUp);
                }}
              >
              {selectedElement?.backgroundImage && (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${selectedElement.backgroundImage})`,
                    backgroundSize: selectedElement.imageScale 
                      ? `${selectedElement.imageScale}%` 
                      : '100%',
                    backgroundPosition: `${selectedElement.imagePositionX || 50}% ${selectedElement.imagePositionY || 50}%`,
                    backgroundRepeat: 'no-repeat',
                    borderRadius: `${selectedElement.style?.borderRadius || 0}px`,
                    border: selectedElement.style?.borderWidth 
                      ? `${selectedElement.style.borderWidth}px ${selectedElement.style.borderStyle || 'solid'} ${selectedElement.style.borderColor || '#000'}` 
                      : 'none'
                  }}
                />
              )}
              </Box>
            </Box>

            {/* Zoom Controls */}
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
              <Tooltip title="Zoom out">
                <IconButton
                  onClick={() =>
                    updateSlideElements(selectedElementId, (element) => ({
                      ...element,
                      imageScale: Math.max(10, (element.imageScale || 100) - 10)
                    }))
                  }
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>−</Typography>
                </IconButton>
              </Tooltip>
              
              <Box sx={{ minWidth: 120, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedElement?.imageScale || 100}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Zoom Level
                </Typography>
              </Box>
              
              <Tooltip title="Zoom in">
                <IconButton
                  onClick={() =>
                    updateSlideElements(selectedElementId, (element) => ({
                      ...element,
                      imageScale: Math.min(300, (element.imageScale || 100) + 10)
                    }))
                  }
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>+</Typography>
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() =>
                  updateSlideElements(selectedElementId, (element) => ({
                    ...element,
                    imageScale: 100,
                    imagePositionX: 50,
                    imagePositionY: 50
                  }))
                }
              >
                Reset to Default
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageAdjustDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => setImageAdjustDialogOpen(false)}
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Box className="pitchdeck-print-area">
        {deck.slides.map((printSlide) => (
          <Box key={printSlide.id} sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderSlideCanvas(printSlide, { preview: false })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PitchDeck;
